// @ts-nocheck
/*
 * Port of views/dashboard/campaigns/create.js (5416 lines). BYTE-IDENTICAL.
 * The compose-campaign wizard: 8 potential steps (dialer, template, general,
 * upload_contacts, script, settings, schedule, sms), gated per campaign type
 * / template combo by buildStepFlow.
 *
 * v1 imports CKEditor + react-select -- both preserved (CKEditor installed for
 * v2 specifically for this port). Rich validation (per-step `computeStepErrors`
 * plus a monolithic `validateForm` on submit), promotional-account 09:00-21:00
 * clamp, same-day start-time grace of 5 minutes, and the mappedCampaign
 * duplicate-campaign hydration flow are preserved verbatim.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Container: any = ({ fluid, className, children, ...props }: any) => (
  <div className={_cx(fluid ? 'container-fluid' : 'container', className)} {...props}>
    {children}
  </div>
);
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
  success: 'success',
  dark: 'default',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
// Missing sub-component that the wizard's footer JSX relies on. Without
// this, `<Card.Footer>` resolves to `undefined` at render time and React
// throws "Element type is invalid". Same shape as Header/Body.
Card.Footer = ({ className, children, ...props }: any) => (
  <div className={_cx('card-footer', className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  style,
  ...props
}: any) => (
  <div
    className={_cx('form-check', inline && 'form-check-inline', className)}
    style={style}
  >
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Table: any = ({
  bordered,
  striped,
  hover,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table
    className={_cx(
      'table',
      bordered && 'table-bordered',
      striped && 'table-striped',
      hover && 'table-hover',
      className,
    )}
    {...props}
  >
    {children}
  </table>
);
const _NavCtx: any = {};
const Nav: any = ({ variant: _v, activeKey, defaultActiveKey, onSelect, children, className, ...props }: any) => {
  return (
    <ul className={_cx('nav', _v === 'tabs' && 'nav-tabs', _v === 'pills' && 'nav-pills', className)} {...props}>
      {React.Children.map(children, (child: any) =>
        React.isValidElement(child) ? React.cloneElement(child as any, { _navActive: activeKey ?? defaultActiveKey, _navOnSelect: onSelect }) : child,
      )}
    </ul>
  );
};
Nav.Item = ({ children, className, ...props }: any) => (
  <li className={_cx('nav-item', className)} {...props}>
    {children}
  </li>
);
Nav.Link = ({ eventKey, active, onClick, onSelect, children, className, _navActive, _navOnSelect, ...props }: any) => {
  const isActive = active ?? (_navActive !== undefined && _navActive === eventKey);
  return (
    <a
      href="#"
      role="tab"
      className={_cx('nav-link', isActive && 'active', className)}
      onClick={(e: any) => {
        e.preventDefault();
        onClick?.(e);
        onSelect?.(eventKey);
        _navOnSelect?.(eventKey);
      }}
      {...props}
    >
      {children}
    </a>
  );
};
import {
  FaArrowLeft,
  FaCommentDots,
  FaCog,
  FaCalendarAlt,
  FaFileAlt,
  FaGlobe,
  FaCheckCircle,
  FaNewspaper,
  FaMicrochip,
  FaWindowClose,
  FaInfoCircle,
  FaUpload,
  FaPlay,
  FaPause,
} from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { canCreateCampaign } from '@/utilities/promptUploadAccess';
import { UploadNotPermitted } from '@/components/feedback/UploadNotPermitted';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  API_AGENTS_LIST,
  API_AGENT_GROUPS_LIST,
  API_VOICE_CAMPAIGNS_CREATE,
  API_VOICE_CAMPAIGNS_EDIT,
  API_IVR_LIST,
  API_NUMBERS_LIST,
  API_CONTACT_GROUP_LIST,
  API_WEBHOOK_LIST,
  API_SMSWEBHOOK_LIST,
  API_SCRIPTS_LIST,
  API_VOICE_CAMPAIGNS_GET_ONE,
  API_COMPANY_PROFILE,
  API_DISPOSITION_LIST,
  API_VOICE_BASE_LIST,
  API_VOICE_BASE_NUMBERS,
  API_AGENT_GROUP_ASSIGNED_AGENTS,
  API_CAMPAIGN_OPTIONS,
} from '@/service/url';
import { getApiCall, patchApiCall, postApiCall } from '@/service/httpCall';
import { canEditCampaignNow, isReadyEditLocked, READY_EDIT_LOCKED_MESSAGE } from './editLock';
import CampaignBasesSection from './CampaignBasesSection';
import {
  AvailabilityHint,
  isCampaignNameAvailable,
  useCampaignNameAvailability,
} from '@/hooks/useAvailability';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { CalendarDateInput, CalendarTimeInput } from '@/components/ui/calendar-inputs';
import PauseSlotsModal, { getPauseWindow, pauseLabel, validatePauseSlots } from './PauseSlotsModal';
import { ROUTES, BACKEND_API_BASE_URL, FLASH_MESSAGE_ACTION_OBJECT, MODULE_ACL, NUMBERS_TYPES_DID, NUMBERS_TYPES_VMN, NUMBERS_TYPES_TOLFREE } from '@/utilities/constant';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getUser } from '@/utilities/setting';
import { useAclVersion } from '@/hooks/useSessionRefresh';
import { getUserAllowedPacingRatios } from '@/utilities/campaignPacingAcl';
import Select from 'react-select';
import {
  getCurrentTime,
  getCurrentDate,
  getScriptTitleValidationError,
  stripHtmlTags,
  getLengthError,
  SCRIPT_SHORT_DESCRIPTION_MAX,
  SCRIPT_DESCRIPTION_MAX,
  SCRIPT_EDITOR_CONFIG,
} from '@/utilities/helpers';
import { formMatchesCallDirection, formatDispositionCallDirectionLabel } from '@/utilities/dispositionCallDirection';
import { fetchAndSetFlashMessage } from '@/service/fetchFlashMessage';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { downloadNumberTemplate } from './NumbersViewerModal';

const getToday = () => new Date().toLocaleDateString('en-CA');
// The "not before now" start-time rule only applies to a one-day campaign starting
// today. A multi-day campaign starting today may pick an earlier From time: it still
// applies on every later day.
const enforcesSameDayStartMin = (c) => c?.start_date === getToday() && c?.start_date === c?.end_date;

function expectedDispositionCallDirectionForDialerType(dialerType) {
  const t = String(dialerType ?? '');
  if (t === '2' || t === '3' || t === '4') return 'outbound';
  if (t === '1') return 'inbound';
  return null;
}

function isTemplateAllowedForDialer(templateOption, dialerValue) {
  const allowed = templateOption?.allowed_dialer_values;
  if (!Array.isArray(allowed) || allowed.length === 0) return true;
  return allowed.map(String).includes(String(dialerValue ?? ''));
}

const CAMPAIGN_OPTIONS_ERROR =
  'Unable to load dialer types and templates. Please refresh or contact support.';

const AUTO_DIALER_SCHEDULE_START = '09:00';
const AUTO_DIALER_SCHEDULE_END = '21:00';
const MAX_MANUAL_CONTACTS = 50000;
const ACCOUNT_TYPE_PROMOTIONAL = 0;

function isPowerDialerCampaign(campaign) {
  return String(campaign?.campaign_type) === '4';
}
function isAutoDialerCampaign(campaign) {
  return String(campaign?.campaign_type) === '2';
}
function usesContactUpload(campaign) {
  return isAutoDialerCampaign(campaign) || isPowerDialerCampaign(campaign);
}
function isPromotionalAccount(user) {
  return Number(user?.account_type) === ACCOUNT_TYPE_PROMOTIONAL;
}
function shouldSkipScriptTab(campaign) {
  return isAutoDialerCampaign(campaign) && ['0', '1'].includes(String(campaign?.template_id ?? ''));
}
function shouldHideDispositionForm(campaign) {
  return usesContactUpload(campaign) && String(campaign?.template_id) === '0';
}
function usesSimpleIvrTemplate(campaign) {
  return String(campaign?.template_id ?? '') === '0';
}
function isClick2CallCampaign(campaign) {
  return String(campaign?.campaign_type) === '1';
}
function usesPowerDialerCallGroups(campaign) {
  return isPowerDialerCampaign(campaign) && String(campaign?.template_id) === '2';
}
function usesCallGroupMultiAssign(campaign) {
  return (
    isClick2CallCampaign(campaign) ||
    (usesPowerDialerCallGroups(campaign) && !!campaign?.enableCallPatchAdditionalSettings) ||
    (isAutoDialerCampaign(campaign) &&
      String(campaign?.template_id) === '2' &&
      !!campaign?.enableCallPatchAdditionalSettings)
  );
}
function usesCallGroupDtmf(campaign) {
  return !isClick2CallCampaign(campaign) && !!campaign?.enableCallPatchAdditionalSettings;
}

const CALL_PATCH_TEMPLATE_VALUE = '2';

function isAgentFirstValue(firstCall) {
  return String(firstCall ?? 'agent').toLowerCase() === 'agent';
}
function isAgentFirstCampaign(campaign) {
  return isAutoDialerCampaign(campaign) && isAgentFirstValue(campaign?.first_call);
}
function supportsCallPatchAdditionalSettings(campaign) {
  return String(campaign?.template_id) === '2' && !isAgentFirstCampaign(campaign);
}
function filterTemplateOptions(templateOptions, campaign) {
  const list = (templateOptions || []).filter((option) =>
    isTemplateAllowedForDialer(option, campaign?.campaign_type),
  );
  if (!isAgentFirstCampaign(campaign)) return list;
  return list.filter((option) => String(option.value) === CALL_PATCH_TEMPLATE_VALUE);
}
function clearCallPatchAdditionalSettings(prev, updated) {
  updated.menu_voice_file = '';
  updated.menu_voice_file_language = '';
  updated.menu_wait_time = '';
  updated.re_prompt = '';
  updated.welcome_file = { enabled: false, selected: '', language: '' };
  updated.no_input_file = { enabled: false, selected: '', language: '' };
  updated.wrong_input_file = { enabled: false, selected: '', language: '' };
  updated.thanks_file = { enabled: false, selected: '', language: '' };
  updated.no_agent_file = { enabled: false, selected: '', language: '' };
  if (Array.isArray(prev.assignedQueues) && prev.assignedQueues.length > 0) {
    const first = prev.assignedQueues[0];
    updated.assignedQueues = [{ ...first, dtmf: null }];
    updated.selectedQueue = first.queueId || '';
  } else {
    updated.assignedQueues = [];
  }
  updated.selectedDtmf = '';
  return updated;
}
// Call Patch always needs a call group: dropdown shown by default, no checkbox.
function isCallGroupForced(campaign) {
  return String(campaign?.template_id) === CALL_PATCH_TEMPLATE_VALUE && !isClick2CallCampaign(campaign);
}
function usesCallPatchSingleCallGroup(campaign) {
  return (
    (isAutoDialerCampaign(campaign) || isPowerDialerCampaign(campaign)) &&
    String(campaign?.template_id) === '2' &&
    !campaign?.enableCallPatchAdditionalSettings
  );
}
function contactGroupLabel(group) {
  return Number(group.member_count) ? group.group_name : `${group.group_name} (No contacts)`;
}
function shouldHideContactGroupOnGeneral(campaign) {
  return isAutoDialerCampaign(campaign) || isClick2CallCampaign(campaign) || isPowerDialerCampaign(campaign);
}
function shouldShowCallGroupCheckboxForTemplate(campaign) {
  if (
    isClick2CallCampaign(campaign) ||
    usesCallGroupMultiAssign(campaign) ||
    usesCallPatchSingleCallGroup(campaign)
  ) {
    return false;
  }
  if (!isAutoDialerCampaign(campaign)) {
    return campaign.template_id == 0 || campaign.template_id == 1 || campaign.campaign_type != 2;
  }
  return false;
}
function buildStepFlow(campaign, isEdit = false) {
  // Consolidated flow (was up to 8 steps):
  //   Dialer + Template + General -> "Basics"
  //   Settings + Schedule           -> "Setup"
  // Per-step validators (computeStepErrors) still cover every field
  // that lived on the old, individual steps.
  const flow: any[] = [{ key: 'basics', label: 'Basics', icon: 'globe' }];
  if (!isEdit && usesContactUpload(campaign)) {
    flow.push({ key: 'upload_contacts', label: 'Upload Contacts', icon: 'upload' });
  }
  if (isEdit && ['2', '3', '4'].includes(String(campaign.campaign_type))) {
    flow.push({ key: 'bases', label: 'Uploaded Bases', icon: 'upload' });
  }
  if (!shouldSkipScriptTab(campaign)) {
    flow.push({ key: 'script', label: 'Script', icon: 'script' });
  }
  flow.push(
    { key: 'setup', label: 'Setup', icon: 'settings' },
    { key: 'sms', label: 'Sms', icon: 'sms' },
  );
  return flow;
}
function parseManualContactNumbers(text) {
  if (!text || !String(text).trim()) return [];
  return [
    ...new Set(
      String(text)
        .split(/[\n,;]+/)
        .map((n) => n.trim().replace(/\D/g, ''))
        .filter((n) => n.length === 10),
    ),
  ];
}

const Req = () => <span style={{ color: 'red', marginRight: '3px' }}>*</span>;
const FieldError = ({ message }: any) =>
  message ? (
    <small className="mt-1 block text-xs font-medium text-destructive">{message}</small>
  ) : null;

// Same text the backend returns from create / check-name.
const CAMPAIGN_NAME_EXISTS_MSG = 'Campaign name already exists, please choose another name.';

/**
 * Card-style radio option: one clickable pill that flows in a row.
 * Replaces the Bootstrap `.form-check + radio dot` UI with something
 * that matches the card-selector language used elsewhere (Company
 * wizard `company-type-card-v2`, dashboard tone chips, etc).
 *
 * Selected → primary-tinted border + soft primary fill + primary text.
 * Idle    → neutral border + card surface.
 */
// When a failed contact is retried (voice_campaigns.retry_mode):
//   on_failure        — during the campaign, retry_interval minutes after the call fails (min 15)
//   after_completion  — once every number has been dialled once, retry all failed numbers (min 1)
const RETRY_MODE_ON_FAILURE = 'on_failure';
const RETRY_MODE_AFTER_COMPLETION = 'after_completion';
const RETRY_INTERVAL_MAX = 120;
const minRetryInterval = (mode) => (mode === RETRY_MODE_AFTER_COMPLETION ? 1 : 15);
const retryIntervalRangeMessage = (mode) =>
  `Retry time interval must be between ${minRetryInterval(mode)} and ${RETRY_INTERVAL_MAX} minutes.`;

function RadioCard({
  name,
  value,
  checked,
  onChange,
  label,
  description,
  icon,
}: any) {
  return (
    <label
      className={
        'group flex flex-1 cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ' +
        (checked
          ? 'border-primary bg-primary/[0.08] text-primary'
          : 'border-border bg-card text-foreground hover:border-primary/40 hover:bg-primary/[0.04]')
      }
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 shrink-0 accent-current"
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-sm font-semibold">
          {icon}
          {label}
        </span>
        {description && (
          <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>
        )}
      </span>
    </label>
  );
}

const START_TIME_GRACE_MINUTES = 5;
const startTimeTooEarlyMessage = (minTime) => `Start time cannot be earlier than ${minTime}.`;
const START_DATE_IN_PAST_MESSAGE = 'Start date cannot be in the past.';
const END_DATE_IN_PAST_MESSAGE = 'End date cannot be in the past.';
const END_TIME_IN_PAST_MESSAGE = 'End time cannot be before the current time when the end date is today.';
// Current time as "HH:mm", for the end-date-is-today rule.
const currentHHMM = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};
// A campaign ending today can't end before now (every account type).
const endsInThePast = (c) =>
  c?.end_date === getToday() &&
  /^\d{2}:\d{2}$/.test(c?.endHour || '') &&
  c.endHour <= currentHHMM();

// Campaign timing mode (voice_campaigns.strict_timings), the "Strict timings" switch on the Schedule step:
//   0 (default) "9 to 21": start date runs from the chosen start time, every
//     later day runs 09:00-21:00 for promotional accounts, all day (00:00-23:59) for
//     transactional ones. A same-day start may be back-dated, but not before 09:00.
//   1 "strict": chosen start/end hours apply every day. A same-day start
//     cannot be more than START_TIME_GRACE_MINUTES in the past.
const isStrictTimings = (value) => Number(value) === 1;

const READY_EDIT_LEAD_MINUTES = 5;
const READY_EDIT_MODE = 'ready_edit';

// Earliest start time allowed when the start date is today.
const getMinStartTime = (strictTimings) => {
  const now = new Date();
  const minutesOfDay = now.getHours() * 60 + now.getMinutes();
  const earliest = strictTimings === READY_EDIT_MODE
    ? Math.min(23 * 60 + 59, minutesOfDay + READY_EDIT_LEAD_MINUTES)
    : isStrictTimings(strictTimings)
    ? Math.max(0, minutesOfDay - START_TIME_GRACE_MINUTES)
    : timeStringToMinutes(AUTO_DIALER_SCHEDULE_START);
  const hours = String(Math.floor(earliest / 60)).padStart(2, '0');
  const minutes = String(earliest % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const getDefaultStartTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const timeStringToMinutes = (time) => {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return null;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const clampToPromotionalWindow = (time) => {
  const t = timeStringToMinutes(time);
  const min = timeStringToMinutes(AUTO_DIALER_SCHEDULE_START);
  const max = timeStringToMinutes(AUTO_DIALER_SCHEDULE_END);
  if (t == null) return AUTO_DIALER_SCHEDULE_START;
  if (t < min) return AUTO_DIALER_SCHEDULE_START;
  if (t > max) return AUTO_DIALER_SCHEDULE_END;
  return time;
};

const getPromotionalDefaultStartTime = (startDate) => {
  if (startDate === getToday()) {
    return clampToPromotionalWindow(getDefaultStartTime());
  }
  return AUTO_DIALER_SCHEDULE_START;
};

const CreateCampaign = () => {
  const { duplicate_camp_id, edit_camp_id } = useParams();
  const isEdit = !!edit_camp_id;
  const sourceCampId = edit_camp_id || duplicate_camp_id;
  const [originalName, setOriginalName] = useState<string | null>(null);
  const [originalStartDate, setOriginalStartDate] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<number | null>(null);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [numbersList, setNumbersList] = useState<any[]>([]);
  // Only DIDs can dial out — VMN / TOLL FREE are incoming-only.
  const outboundDidList = useMemo(
    () => (Array.isArray(numbersList) ? numbersList : []).filter(
      (num) => String(num?.number_type || '').toUpperCase() === NUMBERS_TYPES_DID,
    ),
    [numbersList],
  );
  const dispositionFormTooltipText =
    "Optional. Forms are filtered by dialer type. Forms set to Both appear for inbound and outbound campaigns. If you don't select a disposition form, agents will see only the basic disposition fields: First Name, Last Name, Contact Number, and Remarks.";
  const allDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const [parentAssignedCompany, setParentAssignedCompany] = useState<any>(null);

  const aclVersion = useAclVersion();
  const allowedPacingRatios = useMemo(() => getUserAllowedPacingRatios(getUser()), [aclVersion]);
  const defaultPacingRatio = allowedPacingRatios[0] || '';
  const pacingRatioError = allowedPacingRatios.length
    ? 'Pacing ratio is required for Power Dialer'
    : 'No pacing ratio is allowed on your account. Contact your administrator.';

  const [fieldErrors, setFieldErrors] = useState<any>({});
  const [unlockedUpToIndex, setUnlockedUpToIndex] = useState(0);

  const [campaign, setCampaign] = useState<any>({
    campaign_name: '',
    call_group: [],
    agentAssignment: '',
    selectedQueue: '',
    selectedAgents: [],
    scriptOption: 'new',
    scriptTitle: '',
    scriptDescription: '',
    scriptShortDescription: '',
    selectedScript: '',
    did: [],
    timeBetweenCalls: '',
    enableMaxRetries: true,
    retry_mode: RETRY_MODE_ON_FAILURE,
    max_retries: '',
    retryInterval: '',
    removeDuplicates: true,
    start_date: getCurrentDate(),
    end_date: '',
    weekDays: [],
    startHour: '',
    endHour: '23:59',
    strict_timings: 0,
    // Daily pause slots [{ start_time, end_time }] — saved to campaign_schedule.pause_slots.
    pause_slots: [],
    template_id: '2',
    voice_file: '',
    voice_file_language: '',
    menu_voice_file: '',
    menu_voice_file_language: '',
    dtmf: '',
    menu_wait_time: '',
    re_prompt: '',
    welcome_file: { enabled: false, selected: '', language: '' },
    no_input_file: { enabled: false, selected: '', language: '' },
    wrong_input_file: { enabled: false, selected: '', language: '' },
    thanks_file: { enabled: false, selected: '', language: '' },
    no_agent_file: { enabled: false, selected: '', language: '' },
    selectedDtmf: '',
    assignedQueues: [],
    enableCallPatchAdditionalSettings: false,
    campaign_type: '2',
    first_call: 'customer',
    ivr: '',
    sendSmsOnSuccess: false,
    duration: 1,
    successSmsWebhook: '',
    sendSmsOnFail: false,
    failSmsWebhook: '',
    enableWebhook: false,
    webhook: '',
    enableSmsOnDtmf: false,
    smsOnDtmf: '',
    enableSmsOnPatchSuccess: false,
    smsOnPatchSuccess: '',
    enableSmsOnPatchFailed: false,
    smsOnPatchFailed: '',
    locations: [],
    selectedDispositionForm: null,
    contactUploadMethod: '',
    manualContactNumbers: '',
    existingVoiceBaseId: '',
    base_id: null,
    selectedAgentsForQueue: [],
    pacing_ratio: defaultPacingRatio,
  });
  const startDateChanged = !isEdit || campaign.start_date !== originalStartDate;
  const pausedEdit = isEdit && editStatus === 9;
  const minStartTimeMode = (strictTimings) => (pausedEdit ? 1 : isEdit ? READY_EDIT_MODE : strictTimings);
  // Same-day "not before now": one-day campaign starting today (start date set/changed),
  // or a Ready edit starting today. A Paused edit may keep a start time in the past
  // (user rule 2026-10-23). Mirrors the backend middleware.
  const startMinApplies = (c) => !pausedEdit && (startDateChanged || isEdit) && enforcesSameDayStartMin(c);

  /* ── Inline campaign-name uniqueness feedback (same hook as the user module) ── */
  const campaignNameStatus = useCampaignNameAvailability(campaign.campaign_name ?? '', { originalValue: originalName });

  // Returns false (and flags the field) when the name is already taken.
  // Re-checks directly so a still-pending live check can't be skipped.
  const ensureCampaignNameAvailable = async () => {
    const name = String(campaign.campaign_name || '').trim();
    if (!name) return true;
    if (originalName != null && name.toLowerCase() === originalName.trim().toLowerCase()) return true;
    let available = campaignNameStatus !== 'taken';
    if (available) {
      try {
        available = await isCampaignNameAvailable(name);
      } catch {
        available = true; // check failed — the create API still rejects a duplicate
      }
    }
    if (!available) {
      setFieldErrors((prev) => ({ ...prev, campaign_name: CAMPAIGN_NAME_EXISTS_MSG }));
    }
    return available;
  };

  const [contactUploadFile, setContactUploadFile] = useState<any>(null);
  const [groupAgentsList, setGroupAgentsList] = useState<any[]>([]);
  const [loadingGroupAgents, setLoadingGroupAgents] = useState(false);
  const [voiceBaseList, setVoiceBaseList] = useState<any[]>([]);
  const [existingBasePreview, setExistingBasePreview] = useState<any>(null);

  const stepFlow = useMemo(() => buildStepFlow(campaign, isEdit), [campaign.campaign_type, campaign.template_id, isEdit]);

  // Covers every path that lands on Call Patch (default, template change, duplicate hydration).
  useEffect(() => {
    if (isCallGroupForced(campaign) && campaign.agentAssignment !== 'queue') {
      setCampaign((prev) => ({ ...prev, agentAssignment: 'queue' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.template_id, campaign.campaign_type, campaign.agentAssignment]);
  const currentStepKey = stepFlow[stepIndex]?.key ?? 'dialer';
  const isLastStep = stepIndex >= stepFlow.length - 1;

  useEffect(() => {
    if (stepIndex >= stepFlow.length) {
      setStepIndex(Math.max(0, stepFlow.length - 1));
    }
  }, [stepFlow.length, stepIndex]);

  const [selectAll, setSelectAll] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);

  // Daily window pause slots must fit in (strict -> From/To; else 9–9 promo / all day).
  const currentPauseWindow = () =>
    getPauseWindow({
      strict: isStrictTimings(campaign.strict_timings),
      promotional: isPromotionalAccount(user),
      startDate: campaign.start_date,
      endDate: campaign.end_date,
      startHour: campaign.startHour,
      endHour: campaign.endHour,
    });
  // Pauses only make sense when the campaign runs more than an hour a day; at an hour or
  // less the option is hidden and any slots already picked are ignored (not sent).
  const PAUSE_MIN_WINDOW_MINUTES = 60;
  const pausesAllowed = () => {
    const w = currentPauseWindow();
    if (!w) return false;
    const span = timeStringToMinutes(w.end) - timeStringToMinutes(w.start);
    return span > PAUSE_MIN_WINDOW_MINUTES;
  };
  const pauseSlotsError = () => {
    const slots = campaign.pause_slots || [];
    if (!slots.length || !pausesAllowed()) return '';
    const first = validatePauseSlots(slots, currentPauseWindow()).find(Boolean);
    return first ? `Pause timings: ${first} Open "Pause timings" to fix them.` : '';
  };
  const [callGroupList, setCallGroupList] = useState<any[]>([]);
  const [contactGroupList, setContactGroupList] = useState<any[]>([]);
  // Empty groups can't be dialled (backend rejects them too); a duplicated campaign may still carry one.
  const emptySelectedGroups = contactGroupList
    .filter((g) => !Number(g.member_count) && campaign.call_group?.some((id) => String(id) === String(g.id)))
    .map((g) => g.group_name);
  const emptyGroupsMessage = `No contacts in: ${emptySelectedGroups.join(', ')}. Remove it or add contacts first.`;
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [welcomeFileList, setWelcomeFileList] = useState<any[]>([]);
  const [menuFileList, setMenuFileList] = useState<any[]>([]);
  const [thanksFileList, setThanksFileList] = useState<any[]>([]);
  const [noInputFileList, setNoInputFileList] = useState<any[]>([]);
  const [wrongInputFileList, setWrongInputFileList] = useState<any[]>([]);
  const [noAgentFileList, setNoAgentFileList] = useState<any[]>([]);
  // One shared preview player so starting a voice file stops any other.
  const [previewUrl, setPreviewUrl] = useState('');
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => () => { previewAudioRef.current?.pause(); }, []);
  const [loggedInUser, setLoggedInUser] = useState<any>(null);
  const [error, setError] = useState('');
  const [ivrList, setIvrList] = useState<any[]>([]);
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [smsWebhooks, setSmsWebhooks] = useState<any[]>([]);
  const [scripts, setScripts] = useState<any[]>([]);
  const [invalidSteps, setInvalidSteps] = useState<number[]>([]);
  const [user, setUser] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<any>(null);
  const [dispositionForms, setDispositionForms] = useState<any[]>([]);
  const [dialerTypeOptions, setDialerTypeOptions] = useState<any[]>([]);
  const [templateTypeOptions, setTemplateTypeOptions] = useState<any[]>([]);
  const [campaignOptionsLoaded, setCampaignOptionsLoaded] = useState(false);
  const [campaignOptionsError, setCampaignOptionsError] = useState('');

  const allowedTemplateOptions = useMemo(
    () => filterTemplateOptions(templateTypeOptions, campaign),
    [templateTypeOptions, campaign.campaign_type, campaign.first_call],
  );

  const filteredDispositionForms = useMemo(() => {
    const expected = expectedDispositionCallDirectionForDialerType(campaign.campaign_type);
    if (!expected || !Array.isArray(dispositionForms)) {
      return Array.isArray(dispositionForms) ? dispositionForms : [];
    }
    return dispositionForms.filter((f) =>
      formMatchesCallDirection(f.call_direction, expected),
    );
  }, [dispositionForms, campaign.campaign_type]);

  useEffect(() => {
    const expected = expectedDispositionCallDirectionForDialerType(campaign.campaign_type);
    if (!expected || !campaign.selectedDispositionForm || !Array.isArray(dispositionForms)) return;
    const selectedId = String(campaign.selectedDispositionForm);
    const form = dispositionForms.find((f) => String(f.id) === selectedId);
    if (!form) return;
    if (!formMatchesCallDirection(form.call_direction, expected)) {
      setCampaign((prev) => ({ ...prev, selectedDispositionForm: null }));
    }
  }, [campaign.campaign_type, campaign.selectedDispositionForm, dispositionForms]);

  useEffect(() => {
    if (!campaignOptionsLoaded || isEdit) return; // edit keeps the stored dialer/template
    const enabledDialerValues = dialerTypeOptions.map((o) => String(o.value));
    setCampaign((prev) => {
      const next = { ...prev };
      if (!enabledDialerValues.includes(String(prev.campaign_type ?? ''))) {
        next.campaign_type = enabledDialerValues.length === 1 ? enabledDialerValues[0] : '';
      }
      const allowedTemplateValues = filterTemplateOptions(templateTypeOptions, next).map((o) => String(o.value));
      const currentTemplate = String(prev.template_id ?? '');
      if (!allowedTemplateValues.includes(currentTemplate)) {
        next.template_id = allowedTemplateValues.length === 1 ? allowedTemplateValues[0] : '';
      }
      if (next.campaign_type === prev.campaign_type && next.template_id === prev.template_id) {
        return prev;
      }
      return next;
    });
  }, [campaignOptionsLoaded, dialerTypeOptions, templateTypeOptions, campaign.campaign_type, campaign.template_id, campaign.first_call]);

  useEffect(() => {
    if (!isClick2CallCampaign(campaign) || !campaign.selectedQueue) {
      setGroupAgentsList([]);
      return;
    }
    let cancelled = false;
    setLoadingGroupAgents(true);
    const url = API_AGENT_GROUP_ASSIGNED_AGENTS.replace(':id', campaign.selectedQueue);
    getApiCall(url, {})
      .then((resp) => {
        if (cancelled) return;
        const list = Array.isArray(resp) ? resp : [];
        setGroupAgentsList(
          list.map((item) => ({
            id: item.agent_id ?? item.id,
            name: item.agent_name || item.name || item.username || `Agent ${item.agent_id ?? item.id}`,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setGroupAgentsList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingGroupAgents(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaign.campaign_type, campaign.selectedQueue]);

  const getDateDifference = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(start);
    const e = new Date(end);
    const diffTime = (e as any) - (s as any);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const getWeekDaysInRange = (start, end) => {
    if (!start || !end) return [];
    const days: string[] = [];
    const current = new Date(start);
    const last = new Date(end);
    while (current <= last) {
      const weekday = current.toLocaleDateString('en-US', { weekday: 'long' });
      days.push(weekday);
      current.setDate(current.getDate() + 1);
    }
    return [...new Set(days)];
  };

  const dateDiff = getDateDifference(campaign.start_date, campaign.end_date);
  const allowedDays =
    dateDiff !== null && dateDiff <= 6
      ? getWeekDaysInRange(campaign.start_date, campaign.end_date)
      : allDays;

  const handleDayChange = (day, checked) => {
    setCampaign((prev) => ({
      ...prev,
      weekDays: checked ? [...prev.weekDays, day] : prev.weekDays.filter((d) => d !== day),
    }));
    if (checked) {
      setFieldErrors((prev) => ({ ...prev, weekDays: '' }));
    }
  };

  // Timing mode (voice_campaigns.strict_timings): 0 "9 to 21" (default) / 1 "strict".
  // Switching changes the earliest allowed same-day start, so pull an already-picked
  // start time up to the new minimum instead of leaving an invalid value behind.
  const handleStrictTimingsToggle = (checked) => {
    setCampaign((prev) => {
      const next = { ...prev, strict_timings: checked ? 1 : 0 };
      if (startMinApplies(prev) && /^\d{2}:\d{2}$/.test(prev.startHour || '')) {
        let min = getMinStartTime(minStartTimeMode(next.strict_timings));
        if (isPromotionalAccount(user)) min = clampToPromotionalWindow(min);
        if (timeToMinutes(prev.startHour) < timeToMinutes(min)) next.startHour = min;
      }
      return next;
    });
  };

  const handleSelectAllToggle = () => {
    if (selectAll) {
      setCampaign((prev) => ({ ...prev, weekDays: [] }));
    } else {
      setCampaign((prev) => ({ ...prev, weekDays: allowedDays }));
      setFieldErrors((prev) => ({ ...prev, weekDays: '' }));
    }
    setSelectAll(!selectAll);
  };

  const handleFormSubmit = async () => {
    try {
      setButtonDisable(true);
      if (!validateForm()) {
        setButtonDisable(false);
        return;
      }
      // Check before the contact upload so a taken name doesn't leave an orphan base behind.
      if (!(await ensureCampaignNameAvailable())) {
        toast.error(CAMPAIGN_NAME_EXISTS_MSG);
        setButtonDisable(false);
        return;
      }

      const contactSource = !isEdit && usesContactUpload(campaign) ? await buildContactSource() : null;

      let scriptData: any;
      if (campaign.scriptOption === 'new') {
        scriptData = {
          title: campaign.scriptTitle,
          description: campaign.scriptDescription,
          short_description: campaign.scriptShortDescription,
        };
      } else if (campaign.scriptOption === 'existing') {
        scriptData = campaign.selectedScript;
      } else {
        scriptData = null;
      }

      let selectedAgentsData: any;
      if (campaign.agentAssignment === 'queue') {
        selectedAgentsData = [];
      } else {
        selectedAgentsData = campaign.selectedAgents;
      }

      const isPromo = isPromotionalAccount(user);
      const resolvedStartHour = isPromo ? clampToPromotionalWindow(campaign.startHour) : campaign.startHour;
      const resolvedEndHour = isPromo ? clampToPromotionalWindow(campaign.endHour) : campaign.endHour;
      const resolvedMaxRetries = campaign.enableMaxRetries ? Number(campaign.max_retries) : 0;
      const resolvedRetryInterval = campaign.enableMaxRetries ? Number(campaign.retryInterval) : null;
      const resolvedIsRetry = campaign.enableMaxRetries ? 1 : 0;

      const payload: any = {
        ...campaign,
        max_retries: resolvedMaxRetries,
        is_retry: resolvedIsRetry,
        retryInterval: resolvedRetryInterval,
        retry_interval: resolvedRetryInterval,
        retry_mode: campaign.enableMaxRetries ? campaign.retry_mode || RETRY_MODE_ON_FAILURE : null,
        first_call: isAutoDialerCampaign(campaign)
          ? String(campaign.first_call || 'customer').toLowerCase()
          : null,
        startHour: resolvedStartHour,
        endHour: resolvedEndHour,
        strict_timings: isStrictTimings(campaign.strict_timings) ? 1 : 0,
        selectedAgents: isClick2CallCampaign(campaign)
          ? [...new Set(campaign.assignedQueues.flatMap((q) => q.agentIds || []))]
          : selectedAgentsData,
        agentAssignment: isClick2CallCampaign(campaign) ? 'agents' : campaign.agentAssignment,
        call_group: isClick2CallCampaign(campaign) ? [] : campaign.call_group,
        base_id: null,
        ...(contactSource ? { contact_source: contactSource, remove_duplicates: campaign.removeDuplicates ? 1 : 0 } : {}),
        member_schedule: {
          start_time: resolvedStartHour,
          end_time: resolvedEndHour,
          week_days: campaign.weekDays,
          pause_slots: (pausesAllowed() ? campaign.pause_slots || [] : []).map((s) => ({
            name: (s.name || '').trim(),
            start_time: s.start_time,
            end_time: s.end_time,
          })),
        },
        script: scriptData,
      };

      if (isEdit) {
        patchApiCall(API_VOICE_CAMPAIGNS_EDIT.replace(':id', edit_camp_id), payload).then(onCreateCampSuccess).catch(onCreateCampError);
      } else {
        postApiCall(API_VOICE_CAMPAIGNS_CREATE, payload).then(onCreateCampSuccess).catch(onCreateCampError);
      }
    } catch (error) {
      console.error('Error submitting campaign:', error);
    }
  };

  useEffect(() => {
    if (campaign.agentAssignment === 'all') {
      getApiCall(API_AGENTS_LIST, { fields: 'min' })
        .then((data) => {
          if (data.length > 0) {
            const agentIds = data.map((item) => item.id);
            setCampaign((prev) => ({ ...prev, selectedAgents: agentIds }));
          }
        })
        .catch((error) => {
          toast.error(friendlyApiError(error) || 'Failed to load agents');
        });
    }
  }, [campaign.agentAssignment, campaign.call_group]);

  function fetchVoiceBaseList() {
    getApiCall(API_VOICE_BASE_LIST, {})
      .then((resp) => {
        setVoiceBaseList(Array.isArray(resp) ? resp : []);
      })
      .catch(onError);
  }

  useEffect(() => {
    if (usesContactUpload(campaign)) {
      fetchVoiceBaseList();
    }
  }, [campaign.campaign_type]);

  // Numbers inside the picked existing contact file (read-only preview).
  useEffect(() => {
    const baseId = campaign.contactUploadMethod === 'existing' ? campaign.existingVoiceBaseId : '';
    if (!baseId) {
      setExistingBasePreview(null);
      return undefined;
    }
    let stale = false;
    setExistingBasePreview({ loading: true });
    getApiCall(API_VOICE_BASE_NUMBERS.replace(':id', String(baseId)), {})
      .then((res) => {
        if (stale) return;
        const numbers = Array.isArray(res?.numbers) ? res.numbers : [];
        setExistingBasePreview({
          text: numbers.join('\n'),
          count: numbers.length,
          total: Number(res?.total) || numbers.length,
          truncated: !!res?.truncated,
          warning: res?.warning || null,
        });
      })
      .catch((error) => {
        if (!stale) setExistingBasePreview({ error: friendlyApiError(error) || 'Unable to load numbers for this file.' });
      });
    return () => { stale = true; };
  }, [campaign.contactUploadMethod, campaign.existingVoiceBaseId]);

  async function buildContactSource() {
    const method = campaign.contactUploadMethod;
    if (method === 'manual') return { method, numbers: campaign.manualContactNumbers };
    if (method === 'upload_file') {
      return { method, lines: await contactUploadFile.text() };
    }
    if (method === 'existing') return { method, base_id: Number(campaign.existingVoiceBaseId) };
    return { method: 'contact_group' };
  }

  function onCreateCampSuccess() {
    if (isEdit) {
      toast.success('Outbound Campaign updated successfully', {
        description: 'Changes may take up to 5 minutes to fully sync once the campaign is running.',
      });
    } else {
      toast.success('Outbound Campaign created successfully');
    }
    setTimeout(() => {
      navigate(`/${ROUTES.CAMPAIGNS.LIST}`);
    }, 1500);
  }

  function onCreateCampError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      if (err === CAMPAIGN_NAME_EXISTS_MSG) {
        setFieldErrors((prev) => ({ ...prev, campaign_name: CAMPAIGN_NAME_EXISTS_MSG }));
      }
      toast.error(friendlyApiError(err) || (isEdit ? 'Failed to update campaign' : 'Failed to create campaign'));
    }
  }

  function onCallGroupListSuccess(resp) { setCallGroupList(resp); }
  function onContactGroupListSuccess(resp) { setContactGroupList(resp); }
  function onScriptsListSuccess(resp) { setScripts(resp); }
  function onIvrListSuccess(resp) { setIvrList(resp); }

  function fetchCampaignOptions() {
    getApiCall(API_CAMPAIGN_OPTIONS, {})
      .then((resp) => {
        const dialers = Array.isArray(resp?.dialerTypes) ? resp.dialerTypes : [];
        const templates = Array.isArray(resp?.templateTypes) ? resp.templateTypes : [];
        setDialerTypeOptions(dialers);
        setTemplateTypeOptions(templates);
        setCampaignOptionsError(dialers.length ? '' : CAMPAIGN_OPTIONS_ERROR);
        if (!dialers.length) {
          toast.error('No dialer type is enabled. Please contact support.');
        }
      })
      .catch(() => {
        setDialerTypeOptions([]);
        setTemplateTypeOptions([]);
        setCampaignOptionsError(CAMPAIGN_OPTIONS_ERROR);
        toast.error(CAMPAIGN_OPTIONS_ERROR);
      })
      .finally(() => setCampaignOptionsLoaded(true));
  }

  useEffect(() => {
    getApiCall(API_AGENT_GROUPS_LIST, { fields: 'min' }).then(onCallGroupListSuccess).catch(onError);
    getApiCall(API_CONTACT_GROUP_LIST, {}).then(onContactGroupListSuccess).catch(onError);
    getApiCall(API_IVR_LIST, { fields: 'min' }).then(onIvrListSuccess).catch(onError);
    getApiCall(API_NUMBERS_LIST).then(onNumberListSuccess).catch(onError);
    getApiCall(API_WEBHOOK_LIST).then(onWebhookListSuccess).catch(onError);
    getApiCall(API_SMSWEBHOOK_LIST).then(onSmsWebhookListSuccess).catch(onError);
    getApiCall(API_SCRIPTS_LIST, {}).then(onScriptsListSuccess).catch(onError);
    getApiCall(API_DISPOSITION_LIST, { submissions: 0 }).then(onDispositionFormListSuccess).catch(onError);
    fetchCampaignOptions();

    const resp = getUser();
    if (resp) {
      setLoggedInUser(resp.id);
      setUser(resp);
      if (resp && resp.createdByUser) {
        if (resp.createdByUser.assigned_company) {
          setParentAssignedCompany(resp.createdByUser.assigned_company);
        }
      }
      if (isPromotionalAccount(resp)) {
        setCampaign((prev) => ({
          ...prev,
          startHour: getPromotionalDefaultStartTime(prev.start_date),
          endHour: AUTO_DIALER_SCHEDULE_END,
        }));
      }
    }

    fetchAndSetFlashMessage(
      (msg) =>
        msg.module_name === MODULE_ACL.CAMPAIGNS &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD &&
        msg.type === 'Flash Message',
      setAlertMessage,
    );
  }, []);

  useEffect(() => {
    if (loggedInUser) {
      getApiCall(`${BACKEND_API_BASE_URL}/v2/prompt?prompt_status=1&user_id=${loggedInUser}`)
        .then(onPromptsSuccess)
        .catch(onError);
    }
  }, [loggedInUser]);

  useEffect(() => {
    const fetchParentAssignedCompanyDetails = async () => {
      if (!parentAssignedCompany || !user) return;
      try {
        const url = API_COMPANY_PROFILE.replace(':id', parentAssignedCompany);
        const companyDetails = await getApiCall(url, {});
        if (
          companyDetails?.companyLocations &&
          JSON.stringify(user?.userLocations) !== JSON.stringify(companyDetails.companyLocations)
        ) {
          setUser((prev) => ({ ...prev, userLocations: companyDetails.companyLocations }));
        }
      } catch (error) {
        console.log('error fetching parent assigned company details:', error);
      }
    };
    fetchParentAssignedCompanyDetails();
  }, [parentAssignedCompany]);

  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  function onPromptsSuccess(resp) {
    if (!resp || !Array.isArray(resp)) return;
    const approvedFiles = resp.filter(
      (item) => Array.isArray(item.prompt_url) && item.prompt_url.some((e) => e.status === 1),
    );
    setWelcomeFileList(approvedFiles);
    setMenuFileList(approvedFiles);
    setThanksFileList(approvedFiles);
    setNoInputFileList(approvedFiles);
    setWrongInputFileList(approvedFiles);
    setNoAgentFileList(approvedFiles);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  function onNumberListSuccess(resp) { setNumbersList(resp); }
  function onWebhookListSuccess(resp) { setWebhooks(resp); }
  function onSmsWebhookListSuccess(resp) { setSmsWebhooks(resp); }
  function onDispositionFormListSuccess(resp) { setDispositionForms(resp); }

  const handleChange = (e) => {
    let { name, value, type, checked } = e.target;

    if (name === 'max_retries') {
      const num = parseInt(value, 10);
      if (Number.isNaN(num)) value = '';
      else {
        if (num > 5) value = 5;
        if (num < 1) value = 1;
      }
    }

    if (name === 'retryInterval') {
      const num = parseInt(value, 10);
      if (Number.isNaN(num)) value = '';
      else {
        if (num > 120) value = 120;
        if (num < 1) value = 1;
      }
    }

    if (name === 'enableMaxRetries') {
      setCampaign((prev) => ({
        ...prev,
        enableMaxRetries: checked,
        max_retries: checked ? prev.max_retries || '' : 0,
        retryInterval: checked ? prev.retryInterval || '' : '',
      }));
      if (fieldErrors.max_retries || fieldErrors.retryInterval) {
        setFieldErrors((prev) => ({ ...prev, max_retries: '', retryInterval: '' }));
      }
      return;
    }

    if (name === 'enableCallPatchAdditionalSettings') {
      setCampaign((prev) => {
        const updated: any = { ...prev, enableCallPatchAdditionalSettings: checked };
        if (!checked) clearCallPatchAdditionalSettings(prev, updated);
        return updated;
      });
      return;
    }

    if (name === 'first_call') {
      setCampaign((prev) => {
        const updated: any = { ...prev, first_call: value };
        if (isAgentFirstValue(value)) {
          updated.enableCallPatchAdditionalSettings = false;
          clearCallPatchAdditionalSettings(prev, updated);
          if (String(prev.template_id) !== CALL_PATCH_TEMPLATE_VALUE) {
            updated.template_id = CALL_PATCH_TEMPLATE_VALUE;
            updated.voice_file = '';
            updated.voice_file_language = '';
            updated.dtmf = '';
            updated.ivr = '';
          }
        }
        return updated;
      });
      setFieldErrors((prev) => ({
        ...prev,
        first_call: '',
        ...(isAgentFirstValue(value)
          ? {
              menu_voice_file: '',
              menu_voice_file_language: '',
              menu_wait_time: '',
              re_prompt: '',
              welcome_file: '',
              no_input_file: '',
              wrong_input_file: '',
              thanks_file: '',
              no_agent_file: '',
              no_agent_file_language: '',
            }
          : {}),
      }));
      return;
    }

    if (name === 'timeBetweenCalls') {
      let num = parseInt(value);
      if (num < 0) num = 0;
      if (num > 90) num = 90;
      value = num;
    }

    if (name === 'campaign_name') {
      const onlyValid = value.replace(/[^a-zA-Z0-9_]/g, '');
      if (value !== onlyValid) {
        setError('Only letters, numbers, and underscore (_) are allowed.');
      } else {
        setError('');
      }
      value = onlyValid;
    }

    if (name === 'startHour' || name === 'endHour') {
      if (!/^\d{2}:\d{2}$/.test(value)) {
        setCampaign((prev) => ({ ...prev, [name]: value }));
        return;
      }
      if (isPromotionalAccount(user)) {
        const clamped = clampToPromotionalWindow(value);
        if (clamped !== value) {
          toast.error('Promotional accounts can only schedule between 09:00 and 21:00.');
          value = clamped;
        }
      }
    }

    if (name === 'startHour' && startMinApplies(campaign)) {
      if (!/^\d{2}:\d{2}$/.test(value)) {
        setCampaign((prev) => ({ ...prev, [name]: value }));
        return;
      }
      let minTime = getMinStartTime(minStartTimeMode(campaign.strict_timings));
      if (isPromotionalAccount(user)) minTime = clampToPromotionalWindow(minTime);
      const enteredMin = timeToMinutes(value);
      const minAllowedMin = timeToMinutes(minTime);
      if (enteredMin < minAllowedMin) {
        toast.error(startTimeTooEarlyMessage(minTime));
        return;
      }
    }

    if (name === 'endHour' && campaign.start_date === campaign.end_date) {
      if (!/^\d{2}:\d{2}$/.test(value)) {
        setCampaign((prev) => ({ ...prev, [name]: value }));
        return;
      }
      const start = campaign.startHour;
      if (!start || !/^\d{2}:\d{2}$/.test(start)) {
        toast.error('Please select Start Time first.');
        return;
      }
      const startMin = timeToMinutes(start);
      const endMin = timeToMinutes(value);
      if (campaign.start_date === campaign.end_date) {
        if (endMin <= startMin) {
          toast.error('End time cannot be before start time.');
          return;
        }
      }
    }

    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }

    if (name === 'campaign_type') {
      setStepIndex(0);
      setUnlockedUpToIndex(0);
      setCompletedSteps([]);
      setInvalidSteps([]);
    }

    if (name === 'contactUploadMethod') {
      if (value !== 'upload_file') setContactUploadFile(null);
    }

    setCampaign((prev) => {
      const updated: any = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'campaign_type') {
        const allowedTemplateValues = templateTypeOptions
          .filter((o) => isTemplateAllowedForDialer(o, value))
          .map((o) => String(o.value));
        const prevTemplate = String(prev.template_id ?? '');
        if (prevTemplate !== '' && allowedTemplateValues.includes(prevTemplate)) {
          updated.template_id = prevTemplate;
        } else if (allowedTemplateValues.length === 1) {
          updated.template_id = allowedTemplateValues[0];
        } else {
          updated.template_id = '';
        }
        if (value === '2') {
          updated.first_call = prev.first_call || 'customer';
          if (isPromotionalAccount(user)) {
            updated.startHour = AUTO_DIALER_SCHEDULE_START;
            updated.endHour = AUTO_DIALER_SCHEDULE_END;
          } else {
            updated.startHour = AUTO_DIALER_SCHEDULE_START;
            updated.endHour = AUTO_DIALER_SCHEDULE_END;
          }
        }
        if (String(value) === '4') {
          updated.pacing_ratio = allowedPacingRatios.includes(prev.pacing_ratio) ? prev.pacing_ratio : defaultPacingRatio;
        } else {
          updated.pacing_ratio = '';
        }
        if (String(value) !== '1') {
          updated.assignedQueues = [];
          updated.selectedAgentsForQueue = [];
        }
      }

      if (name === 'template_id' && updated.campaign_type === '2') {
        updated.startHour = AUTO_DIALER_SCHEDULE_START;
        updated.endHour = AUTO_DIALER_SCHEDULE_END;
        if (String(value) === '2') {
          updated.enableCallPatchAdditionalSettings = prev.enableCallPatchAdditionalSettings || false;
        }
      }

      if ((name === 'start_date' || name === 'end_date') && updated.start_date && updated.end_date) {
        if (updated.start_date === updated.end_date) {
          const dateObj = new Date(updated.start_date);
          const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dateObj.getDay()];
          updated.weekDays = [weekday];
          if (updated.start_date === getToday()) {
            if (isPromotionalAccount(user)) {
              updated.startHour = getPromotionalDefaultStartTime(updated.start_date);
              updated.endHour = AUTO_DIALER_SCHEDULE_END;
            } else {
              // Now, but never earlier than the mode's minimum (09:00 in 9-to-9 mode).
              const now = getDefaultStartTime();
              const min = getMinStartTime(minStartTimeMode(updated.strict_timings));
              updated.startHour = timeStringToMinutes(now) < timeStringToMinutes(min) ? min : now;
            }
          } else if (isPromotionalAccount(user)) {
            updated.startHour = AUTO_DIALER_SCHEDULE_START;
            updated.endHour = AUTO_DIALER_SCHEDULE_END;
          }
        } else {
          updated.weekDays = prev.weekDays || [];
        }
      }

      if (isPromotionalAccount(user) && (name === 'startHour' || name === 'endHour')) {
        updated[name] = clampToPromotionalWindow(updated[name]);
      }

      if (name === 'start_date' || name === 'end_date') {
        if (isPromotionalAccount(user) && updated.start_date !== updated.end_date) {
          if (!updated.startHour) updated.startHour = AUTO_DIALER_SCHEDULE_START;
          else updated.startHour = clampToPromotionalWindow(updated.startHour);
          if (!updated.endHour) updated.endHour = AUTO_DIALER_SCHEDULE_END;
          else updated.endHour = clampToPromotionalWindow(updated.endHour);
        }
      }

      if (
        name === 'manualContactNumbers' ||
        name === 'existingVoiceBaseId' ||
        name === 'removeDuplicates'
      ) {
        updated.base_id = null;
      }

      if (name === 'contactUploadMethod') {
        if (value !== 'contact_group') {
          updated.call_group = [];
        }
        if (value === 'contact_group') {
          updated.base_id = null;
          updated.existingVoiceBaseId = '';
          updated.manualContactNumbers = '';
        } else {
          updated.base_id = null;
          updated.existingVoiceBaseId = '';
        }
        if (value !== 'manual') updated.manualContactNumbers = '';
      }

      return updated;
    });
  };

  const getApprovedLanguages = (promptId, list) => {
    const sel = list.find((f) => f.id?.toString() === promptId?.toString());
    if (!sel || !Array.isArray(sel.prompt_url)) return [];
    return sel.prompt_url.filter((e) => e.status === 1);
  };

  const stopPromptPreview = () => {
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    setPreviewUrl('');
  };

  const togglePromptPreview = (url) => {
    const wasPlaying = previewUrl === url;
    stopPromptPreview();
    if (!url || wasPlaying) return;
    const audio = new Audio(url);
    audio.onended = stopPromptPreview;
    audio.play().catch(() => {
      stopPromptPreview();
      toast.error('Unable to play this voice file');
    });
    previewAudioRef.current = audio;
    setPreviewUrl(url);
  };

  // Plays the selected language's recording, or the first approved one
  // when no language is picked yet.
  const renderPromptPlayButton = (promptId, language, list) => {
    const langs = getApprovedLanguages(promptId, list);
    const url = (langs.find((e) => e.language === language) || langs[0])?.url || '';
    if (!url) return null;
    const playing = previewUrl === url;
    return (
      <button
        type="button"
        onClick={() => togglePromptPreview(url)}
        title={playing ? 'Pause' : 'Play'}
        aria-label={playing ? 'Pause voice file' : 'Play voice file'}
        style={{
          flexShrink: 0,
          width: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '6px',
          border: '1px solid #ced4da',
          background: 'transparent',
          color: '#0d6efd',
          cursor: 'pointer',
        }}
      >
        {playing ? <FaPause size={12} /> : <FaPlay size={12} />}
      </button>
    );
  };

  const handlePromptFieldChange = (fieldName, langFieldName, list) => (e) => {
    const value = e.target.value;
    const approvedLangs = getApprovedLanguages(value, list);
    const autoLang = approvedLangs.length === 1 ? approvedLangs[0].language : '';
    setCampaign((prev) => ({ ...prev, [fieldName]: value, [langFieldName]: autoLang }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: '', [langFieldName]: '' }));
  };

  const handlePromptLanguageFieldChange = (langFieldName) => (e) => {
    const value = e.target.value;
    setCampaign((prev) => ({ ...prev, [langFieldName]: value }));
    if (value) setFieldErrors((prev) => ({ ...prev, [langFieldName]: '' }));
  };

  const renderPromptLanguageSelect = (fieldName, langFieldName, list, errorKey) => {
    const langs = getApprovedLanguages(campaign[fieldName], list);
    if (!campaign[fieldName] || langs.length === 0) return null;
    return (
      <>
        <Form.Select
          value={campaign[langFieldName] || ''}
          onChange={handlePromptLanguageFieldChange(langFieldName)}
          style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.75rem',
            borderRadius: '6px',
            border: fieldErrors[errorKey] ? '1px solid red' : '1px solid #ced4da',
          }}
        >
          <option value="">Select Language</option>
          {langs.map((e) => (
            <option key={e.language} value={e.language}>
              {e.language?.toUpperCase()}
              {e.name ? ` — ${e.name}` : ''}
            </option>
          ))}
        </Form.Select>
        <FieldError message={fieldErrors[errorKey]} />
      </>
    );
  };

  const handleNestedPromptFieldChange = (fieldName, list) => (e) => {
    const value = e.target.value;
    const approvedLangs = getApprovedLanguages(value, list);
    const autoLang = approvedLangs.length === 1 ? approvedLangs[0].language : '';
    setCampaign((prev) => ({
      ...prev,
      [fieldName]: { ...prev[fieldName], selected: value, language: autoLang },
    }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: '', [`${fieldName}_language`]: '' }));
  };

  const handleNestedPromptLanguageFieldChange = (fieldName) => (e) => {
    const value = e.target.value;
    setCampaign((prev) => ({ ...prev, [fieldName]: { ...prev[fieldName], language: value } }));
    if (value) setFieldErrors((prev) => ({ ...prev, [`${fieldName}_language`]: '' }));
  };

  const renderNestedPromptLanguageSelect = (fieldName, list) => {
    const langs = getApprovedLanguages(campaign[fieldName]?.selected, list);
    if (!campaign[fieldName]?.selected || langs.length === 0) return null;
    const errorKey = `${fieldName}_language`;
    return (
      <>
        <Form.Select
          value={campaign[fieldName]?.language || ''}
          onChange={handleNestedPromptLanguageFieldChange(fieldName)}
          style={{
            marginTop: '0.5rem',
            border: fieldErrors[errorKey] ? '1px solid red' : '1px solid #ced4da',
          }}
        >
          <option value="">Select Language</option>
          {langs.map((e) => (
            <option key={e.language} value={e.language}>
              {e.language?.toUpperCase()}
              {e.name ? ` — ${e.name}` : ''}
            </option>
          ))}
        </Form.Select>
        <FieldError message={fieldErrors[errorKey]} />
      </>
    );
  };

  const computeStepErrors = (stepKey) => {
    // Merged step-flow keys — recursively pull in the sub-step errors.
    if (stepKey === 'basics') {
      return {
        ...computeStepErrors('dialer'),
        ...computeStepErrors('template'),
        ...computeStepErrors('general'),
      };
    }
    if (stepKey === 'type') {
      return {
        ...computeStepErrors('dialer'),
        ...computeStepErrors('template'),
      };
    }
    if (stepKey === 'setup') {
      return {
        ...computeStepErrors('settings'),
        ...computeStepErrors('schedule'),
      };
    }
    const errors: any = {};
    switch (stepKey) {
      case 'dialer':
        if (!String(campaign.campaign_type ?? '').trim()) errors.campaign_type = 'Dialer Type is required.';
        if (
          isAutoDialerCampaign(campaign) &&
          !['agent', 'customer'].includes(String(campaign.first_call || '').toLowerCase())
        ) {
          errors.first_call = 'Please select Agent First or Customer First.';
        }
        break;
      case 'template':
        if (campaign.campaign_type == 2) {
          if (!String(campaign.template_id ?? '').trim()) errors.template_id = 'Template is required.';
          else if (isAgentFirstCampaign(campaign) && String(campaign.template_id) !== CALL_PATCH_TEMPLATE_VALUE) {
            errors.template_id = 'Agent First supports the Call Patch template only.';
          }
        }
        break;
      case 'general':
        if (!campaign.campaign_name.trim()) errors.campaign_name = 'Campaign name is required.';
        else if (campaignNameStatus === 'taken') {
          errors.campaign_name = CAMPAIGN_NAME_EXISTS_MSG;
        }
        if (isPowerDialerCampaign(campaign)) {
          if (!allowedPacingRatios.includes(String(campaign.pacing_ratio || ''))) {
            errors.pacing_ratio = pacingRatioError;
          }
        }
        if (isEdit) {
        } else if (!shouldHideContactGroupOnGeneral(campaign) && campaign.call_group.length === 0) {
          errors.call_group = 'Contact group is required.';
        } else if (!shouldHideContactGroupOnGeneral(campaign) && emptySelectedGroups.length > 0) {
          errors.call_group = emptyGroupsMessage;
        }
        if (isClick2CallCampaign(campaign)) {
          if (campaign.assignedQueues.length === 0) {
            errors.assignedQueues = 'At least one call group with agents is required.';
          } else if (
            campaign.assignedQueues.some((q) => !Array.isArray(q.agentIds) || q.agentIds.length === 0)
          ) {
            errors.assignedQueues = 'Each call group must have at least one agent selected.';
          }
        }
        if (isPowerDialerCampaign(campaign)) {
          if (campaign.assignedQueues.length === 0) errors.assignedQueues = 'At least one call group is required.';
          else if (usesCallPatchSingleCallGroup(campaign) && campaign.assignedQueues.length > 1) {
            errors.assignedQueues = 'Only one call group is allowed when Additional Settings is disabled.';
          } else if (
            usesCallGroupDtmf(campaign) &&
            campaign.assignedQueues.some((q) => q.dtmf === undefined || q.dtmf === null || q.dtmf === '')
          ) {
            errors.assignedQueues = 'Each call group must have a DTMF digit.';
          }
          if (campaign.enableCallPatchAdditionalSettings) {
            if (!campaign.no_agent_file?.selected) errors.no_agent_file = 'No agent file is required.';
            else if (
              getApprovedLanguages(campaign.no_agent_file.selected, noAgentFileList).length > 1 &&
              !campaign.no_agent_file.language
            ) {
              errors.no_agent_file_language = 'Please select a language.';
            }
            if (!campaign.menu_voice_file) errors.menu_voice_file = 'Menu voice file is required.';
            if (!campaign.menu_wait_time && campaign.menu_wait_time !== 0) errors.menu_wait_time = 'Menu wait time is required.';
            if (!campaign.re_prompt && campaign.re_prompt !== 0) errors.re_prompt = 'Re-prompt is required.';
          }
        }
        if (usesSimpleIvrTemplate(campaign) && !campaign.voice_file) errors.voice_file = 'Voice file is required.';
        if (
          campaign.voice_file &&
          getApprovedLanguages(campaign.voice_file, welcomeFileList).length > 1 &&
          !campaign.voice_file_language
        ) {
          errors.voice_file_language = 'Please select a language.';
        }
        if (campaign.campaign_type == 2) {
          if (campaign.template_id == 0) {
            if (!campaign.voice_file) errors.voice_file = 'Voice file is required.';
          } else if (campaign.template_id == 1) {
            if (!campaign.menu_voice_file) errors.menu_voice_file = 'Menu voice file is required.';
            if (!campaign.dtmf || campaign.dtmf.length === 0) errors.dtmf = 'DTMF is required.';
            if (!campaign.menu_wait_time) errors.menu_wait_time = 'Menu wait time is required.';
            if (!campaign.re_prompt) errors.re_prompt = 'Re-prompt is required.';
            if (campaign.welcome_file.enabled && !campaign.welcome_file.selected) errors.welcome_file = 'Welcome file is required.';
            if (campaign.no_input_file.enabled && !campaign.no_input_file.selected) errors.no_input_file = 'No input file is required.';
            if (campaign.wrong_input_file.enabled && !campaign.wrong_input_file.selected) errors.wrong_input_file = 'Wrong input file is required.';
            if (campaign.thanks_file.enabled && !campaign.thanks_file.selected) errors.thanks_file = 'Thanks file is required.';
          } else if (campaign.template_id == 2) {
            if (campaign.enableCallPatchAdditionalSettings) {
              if (!campaign.no_agent_file?.selected) errors.no_agent_file = 'No agent file is required.';
              else if (
                getApprovedLanguages(campaign.no_agent_file.selected, noAgentFileList).length > 1 &&
                !campaign.no_agent_file.language
              ) {
                errors.no_agent_file_language = 'Please select a language.';
              }
              if (!campaign.menu_voice_file) errors.menu_voice_file = 'Menu voice file is required.';
              if (!campaign.menu_wait_time && campaign.menu_wait_time !== 0) errors.menu_wait_time = 'Menu wait time is required.';
              if (!campaign.re_prompt && campaign.re_prompt !== 0) errors.re_prompt = 'Re-prompt is required.';
              if (campaign.welcome_file.enabled && !campaign.welcome_file.selected) errors.welcome_file = 'Welcome file is required.';
              if (campaign.no_input_file.enabled && !campaign.no_input_file.selected) errors.no_input_file = 'No input file is required.';
              if (campaign.wrong_input_file.enabled && !campaign.wrong_input_file.selected) errors.wrong_input_file = 'Wrong input file is required.';
              if (campaign.thanks_file.enabled && !campaign.thanks_file.selected) errors.thanks_file = 'Thanks file is required.';
            }
            if (campaign.assignedQueues.length === 0) errors.assignedQueues = 'At least one assigned call group is required.';
            else if (!campaign.enableCallPatchAdditionalSettings && campaign.assignedQueues.length > 1) {
              errors.assignedQueues = 'Only one call group is allowed when Additional Settings is disabled.';
            } else if (
              campaign.enableCallPatchAdditionalSettings &&
              campaign.assignedQueues.some((q) => q.dtmf === undefined || q.dtmf === null || q.dtmf === '')
            ) {
              errors.assignedQueues = 'Each assigned call group must have a DTMF digit.';
            }
          }
          if (campaign.template_id == 1 || (campaign.template_id == 2 && campaign.enableCallPatchAdditionalSettings)) {
            if (
              campaign.menu_voice_file &&
              getApprovedLanguages(campaign.menu_voice_file, menuFileList).length > 1 &&
              !campaign.menu_voice_file_language
            ) {
              errors.menu_voice_file_language = 'Please select a language.';
            }
            [
              ['welcome_file', welcomeFileList],
              ['no_input_file', noInputFileList],
              ['wrong_input_file', wrongInputFileList],
              ['thanks_file', thanksFileList],
            ].forEach(([key, list]: any) => {
              const field = campaign[key];
              if (field?.selected && getApprovedLanguages(field.selected, list).length > 1 && !field.language) {
                errors[`${key}_language`] = 'Please select a language.';
              }
            });
          }
          if (campaign.template_id == 3) {
            if (!campaign.ivr) errors.ivr = 'IVR selection is required.';
          }
        }
        break;
      case 'upload_contacts':
        if (!campaign.contactUploadMethod) errors.contactUploadMethod = 'Please select a contact upload method.';
        else if (campaign.contactUploadMethod === 'manual') {
          const numbers = parseManualContactNumbers(campaign.manualContactNumbers);
          if (numbers.length === 0) errors.manualContactNumbers = 'Enter at least one valid 10-digit contact number.';
          else if (numbers.length > MAX_MANUAL_CONTACTS) errors.manualContactNumbers = `Maximum ${MAX_MANUAL_CONTACTS} contacts allowed.`;
        } else if (campaign.contactUploadMethod === 'upload_file') {
          if (!contactUploadFile) errors.contactUploadFile = 'Please choose a file to upload.';
        } else if (campaign.contactUploadMethod === 'existing') {
          if (!campaign.existingVoiceBaseId) errors.existingVoiceBaseId = 'Please select an existing contact file.';
        } else if (campaign.contactUploadMethod === 'contact_group') {
          if (!campaign.call_group?.length) errors.call_group = 'Please select a contact group.';
          else if (emptySelectedGroups.length > 0) errors.call_group = emptyGroupsMessage;
        }
        break;
      case 'script':
        if (campaign.scriptOption === 'new') {
          if (!campaign.scriptTitle.trim()) errors.scriptTitle = 'Script title is required.';
          else {
            const titleError = getScriptTitleValidationError(campaign.scriptTitle);
            if (titleError) errors.scriptTitle = titleError;
          }
          if (!campaign.scriptDescription.trim()) errors.scriptDescription = 'Script description is required.';
          else {
            const descError = getLengthError(campaign.scriptDescription, SCRIPT_DESCRIPTION_MAX, 'Script description');
            if (descError) errors.scriptDescription = descError;
          }
          if (!campaign.scriptShortDescription.trim()) errors.scriptShortDescription = 'Short description is required.';
          else {
            const shortError = getLengthError(campaign.scriptShortDescription, SCRIPT_SHORT_DESCRIPTION_MAX, 'Short description');
            if (shortError) errors.scriptShortDescription = shortError;
          }
        } else if (campaign.scriptOption === 'existing') {
          if (!campaign.selectedScript) errors.selectedScript = 'Please select an existing script.';
        }
        break;
      case 'settings':
        if (campaign.did.length === 0) errors.did = 'At least one DID is required.';
        if (!campaign.timeBetweenCalls && campaign.timeBetweenCalls !== 0) errors.timeBetweenCalls = 'Time between calls is required.';
        if (campaign.enableMaxRetries) {
          if (campaign.max_retries === '' || campaign.max_retries === null || campaign.max_retries === undefined) {
            errors.max_retries = 'Max retries is required.';
          } else if (Number(campaign.max_retries) < 1 || Number(campaign.max_retries) > 5) {
            errors.max_retries = 'Max retries must be between 1 and 5.';
          }
          if (campaign.retryInterval === '' || campaign.retryInterval === null || campaign.retryInterval === undefined) {
            errors.retryInterval = 'Retry time interval is required.';
          } else if (
            Number(campaign.retryInterval) < minRetryInterval(campaign.retry_mode) ||
            Number(campaign.retryInterval) > RETRY_INTERVAL_MAX
          ) {
            errors.retryInterval = retryIntervalRangeMessage(campaign.retry_mode);
          }
        }
        break;
      case 'schedule':
        if (!campaign.start_date) errors.start_date = 'Start date is required.';
        if (!campaign.end_date) errors.end_date = 'End date is required.';
        if (campaign.weekDays.length === 0) errors.weekDays = 'At least one week day is required.';
        if (!campaign.startHour) errors.startHour = 'Start hour is required.';
        if (!campaign.endHour) errors.endHour = 'End hour is required.';
        if (campaign.start_date && campaign.end_date && campaign.end_date < campaign.start_date) {
          errors.end_date = 'End date cannot be before start date.';
        }
        if (isEdit && campaign.end_date && campaign.end_date < getToday()) errors.end_date = END_DATE_IN_PAST_MESSAGE;
        if (startDateChanged && campaign.start_date && campaign.start_date < getToday()) errors.start_date = START_DATE_IN_PAST_MESSAGE;
        {
          const pauseError = pauseSlotsError();
          if (pauseError) errors.pause_slots = pauseError;
        }
        if (startMinApplies(campaign) && campaign.startHour && /^\d{2}:\d{2}$/.test(campaign.startHour)) {
          let minTime = getMinStartTime(minStartTimeMode(campaign.strict_timings));
          if (isPromotionalAccount(user)) minTime = clampToPromotionalWindow(minTime);
          const enteredMin = timeToMinutes(campaign.startHour);
          const minAllowedMin = timeToMinutes(minTime);
          if (enteredMin < minAllowedMin) errors.startHour = startTimeTooEarlyMessage(minTime);
        }
        if (isPromotionalAccount(user) && campaign.startHour && /^\d{2}:\d{2}$/.test(campaign.startHour)) {
          const start = timeToMinutes(campaign.startHour);
          const windowStart = timeToMinutes(AUTO_DIALER_SCHEDULE_START);
          const windowEnd = timeToMinutes(AUTO_DIALER_SCHEDULE_END);
          if (start < windowStart || start > windowEnd) {
            errors.startHour = 'Promotional accounts can only schedule between 09:00 and 21:00.';
          }
        }
        if (isPromotionalAccount(user) && campaign.endHour && /^\d{2}:\d{2}$/.test(campaign.endHour)) {
          const end = timeToMinutes(campaign.endHour);
          const windowStart = timeToMinutes(AUTO_DIALER_SCHEDULE_START);
          const windowEnd = timeToMinutes(AUTO_DIALER_SCHEDULE_END);
          if (end < windowStart || end > windowEnd) {
            errors.endHour = 'Promotional accounts can only schedule between 09:00 and 21:00.';
          }
        }
        if (
          campaign.start_date &&
          campaign.end_date &&
          campaign.startHour &&
          campaign.endHour &&
          /^\d{2}:\d{2}$/.test(campaign.startHour) &&
          /^\d{2}:\d{2}$/.test(campaign.endHour) &&
          campaign.start_date === campaign.end_date
        ) {
          const start = timeToMinutes(campaign.startHour);
          const end = timeToMinutes(campaign.endHour);
          if (end <= start) {
            errors.endHour = 'End Outbound Campaign Hour cannot be before Start Outbound Campaign Hour';
          }
        }
        if (!errors.endHour && endsInThePast(campaign)) errors.endHour = END_TIME_IN_PAST_MESSAGE;
        break;
      case 'sms':
        if (campaign.sendSmsOnSuccess && !campaign.successSmsWebhook) errors.successSmsWebhook = 'Please select a Success SMS Webhook.';
        if (campaign.sendSmsOnFail && !campaign.failSmsWebhook) errors.failSmsWebhook = 'Please select a Fail SMS Webhook.';
        if (campaign.enableWebhook && !campaign.webhook) errors.webhook = 'Please select a Webhook.';
        if (campaign.enableSmsOnDtmf && !campaign.smsOnDtmf) errors.smsOnDtmf = 'Please select a DTMF SMS Webhook.';
        if (campaign.enableSmsOnPatchSuccess && !campaign.smsOnPatchSuccess) errors.smsOnPatchSuccess = 'Please select a Patch Success SMS Webhook.';
        if (campaign.enableSmsOnPatchFailed && !campaign.smsOnPatchFailed) errors.smsOnPatchFailed = 'Please select a Patch Fail SMS Webhook.';
        break;
      default:
        break;
    }
    return errors;
  };

  const handleTabClick = (targetIndex) => {
    if (targetIndex <= unlockedUpToIndex) {
      setStepIndex(targetIndex);
      return;
    }
    toast.warning('Please complete the current step before proceeding.');
  };

  const nextStep = async () => {
    const errors = computeStepErrors(currentStepKey);
    const markInvalid = () => {
      if (!invalidSteps.includes(stepIndex)) setInvalidSteps((prev) => [...prev, stepIndex]);
      if (completedSteps.includes(stepIndex)) setCompletedSteps((prev) => prev.filter((s) => s !== stepIndex));
    };
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      markInvalid();
      return;
    }
    // The campaign name lives on the general step (merged into 'basics' in the step flow).
    if (['general', 'basics'].includes(currentStepKey) && !(await ensureCampaignNameAvailable())) {
      markInvalid();
      return;
    }
    setFieldErrors({});
    if (invalidSteps.includes(stepIndex)) setInvalidSteps((prev) => prev.filter((s) => s !== stepIndex));
    if (!completedSteps.includes(stepIndex)) setCompletedSteps((prev) => [...prev, stepIndex]);
    const nextIndex = stepIndex + 1;
    if (nextIndex < stepFlow.length) {
      if (nextIndex > unlockedUpToIndex) setUnlockedUpToIndex(nextIndex);
      setStepIndex(nextIndex);
    }
  };

  const prevStep = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  function validateForm() {
    let valid = true;
    if (!String(campaign?.template_id ?? '').trim() && campaign.campaign_type == 2) {
      valid = false;
      toast.error('Template is required');
    } else if (isAgentFirstCampaign(campaign) && String(campaign.template_id) !== CALL_PATCH_TEMPLATE_VALUE) {
      valid = false;
      toast.error('Agent First supports the Call Patch template only');
    } else if (!campaign?.campaign_name?.trim()) {
      valid = false;
      toast.error('Outbound Campaign Name required');
    } else if (
      isPowerDialerCampaign(campaign) &&
      !allowedPacingRatios.includes(String(campaign.pacing_ratio || ''))
    ) {
      valid = false;
      toast.error(pacingRatioError);
    } else if (!isEdit && !shouldHideContactGroupOnGeneral(campaign) && !(campaign.call_group.length > 0)) {
      valid = false;
      toast.error('Call Group required');
    } else if (isClick2CallCampaign(campaign) && campaign.assignedQueues.length === 0) {
      valid = false;
      toast.error('At least one call group with agents is required');
    } else if (
      isClick2CallCampaign(campaign) &&
      campaign.assignedQueues.some((q) => !Array.isArray(q.agentIds) || q.agentIds.length === 0)
    ) {
      valid = false;
      toast.error('Each call group must have at least one agent selected');
    } else if (!isEdit && usesContactUpload(campaign) && !campaign.contactUploadMethod) {
      valid = false;
      toast.error('Contact upload method is required');
    } else if (
      !isEdit &&
      usesContactUpload(campaign) &&
      campaign.contactUploadMethod === 'existing' &&
      !campaign.existingVoiceBaseId
    ) {
      valid = false;
      toast.error('Please select an existing contact file');
    } else if (
      !isEdit &&
      usesContactUpload(campaign) &&
      campaign.contactUploadMethod === 'contact_group' &&
      !campaign.call_group?.length
    ) {
      valid = false;
      toast.error('Please select a contact group');
    } else if (!isEdit && !isClick2CallCampaign(campaign) && emptySelectedGroups.length > 0) {
      valid = false;
      toast.error(emptyGroupsMessage);
    } else if (campaign.agentAssignment === 'agents') {
      if (campaign.selectedAgents.length < 1) {
        valid = false;
        toast.error('Selected Agents required');
      }
    } else if (!shouldSkipScriptTab(campaign) && campaign.scriptOption === 'new') {
      if (!campaign?.scriptTitle?.trim()) {
        valid = false;
        toast.error('New Script Title required');
      } else {
        const titleError = getScriptTitleValidationError(campaign.scriptTitle);
        if (titleError) {
          valid = false;
          toast.error(titleError);
        } else if (!campaign?.scriptDescription?.trim()) {
          valid = false;
          toast.error('New Script Description required');
        } else if (!campaign?.scriptShortDescription?.trim()) {
          valid = false;
          toast.error('New Script Short Description required');
        } else {
          const lengthError =
            getLengthError(campaign.scriptDescription, SCRIPT_DESCRIPTION_MAX, 'Script description') ||
            getLengthError(campaign.scriptShortDescription, SCRIPT_SHORT_DESCRIPTION_MAX, 'Short description');
          if (lengthError) {
            valid = false;
            toast.error(lengthError);
          }
        }
      }
    } else if (!shouldSkipScriptTab(campaign) && campaign.scriptOption === 'existing') {
      if (!campaign.selectedScript) {
        valid = false;
        toast.error('Existing Script required');
      }
    } else if (!(campaign.did.length > 0)) {
      valid = false;
      toast.error('Outbound Campaign DID required');
    } else if (!campaign.timeBetweenCalls) {
      valid = false;
      toast.error('Time Between Calls required');
    } else if (
      campaign.enableMaxRetries &&
      (campaign.max_retries === '' || campaign.max_retries === null || campaign.max_retries === undefined)
    ) {
      valid = false;
      toast.error('Max Retries required');
    } else if (
      campaign.enableMaxRetries &&
      (Number(campaign.max_retries) < 1 || Number(campaign.max_retries) > 5)
    ) {
      valid = false;
      toast.error('Max Retries must be between 1 and 5');
    } else if (
      campaign.enableMaxRetries &&
      (campaign.retryInterval === '' || campaign.retryInterval === null || campaign.retryInterval === undefined)
    ) {
      valid = false;
      toast.error('Retry time interval is required');
    } else if (
      campaign.enableMaxRetries &&
      (Number(campaign.retryInterval) < minRetryInterval(campaign.retry_mode) ||
        Number(campaign.retryInterval) > RETRY_INTERVAL_MAX)
    ) {
      valid = false;
      toast.error(retryIntervalRangeMessage(campaign.retry_mode));
    } else if (
      isAutoDialerCampaign(campaign) &&
      !['agent', 'customer'].includes(String(campaign.first_call || '').toLowerCase())
    ) {
      valid = false;
      toast.error('Please select Agent First or Customer First');
    } else if (!campaign.start_date) {
      valid = false;
      toast.error('Outbound Campaign Start Date required');
    } else if (!campaign.end_date) {
      valid = false;
      toast.error('Outbound Campaign End Date required');
    } else if (campaign.weekDays.length < 1) {
      valid = false;
      toast.error('At least 1 Week Day for outbound campaign is required');
    } else if (!campaign.startHour) {
      valid = false;
      toast.error('Outbound Campaign Start Hour required');
    } else if (!campaign.endHour) {
      valid = false;
      toast.error('Outbound Campaign End Hour required');
    } else if (usesSimpleIvrTemplate(campaign) && !campaign.voice_file) {
      valid = false;
      toast.error('Outbound Campaign Voice File is required');
    } else if (
      isAutoDialerCampaign(campaign) &&
      (String(campaign.template_id) === '1' || String(campaign.template_id) === '2')
    ) {
      if (campaign.template_id == 1) {
        if (!campaign.menu_voice_file) {
          valid = false;
          toast.error('Outbound Campaign Menu Voice File is required');
        } else if (!campaign.dtmf) {
          valid = false;
          toast.error('DTMF is required');
        } else if (!campaign.menu_wait_time && campaign.menu_wait_time !== 0) {
          valid = false;
          toast.error('Menu Wait Time is required');
        } else if (!campaign.re_prompt && campaign.re_prompt !== 0) {
          valid = false;
          toast.error('Re-Prompt is required');
        } else if (campaign.welcome_file.enabled && !campaign.welcome_file.selected) {
          valid = false;
          toast.error('Welcome File is require');
        } else if (campaign.no_input_file.enabled && !campaign.no_input_file.selected) {
          valid = false;
          toast.error('No Input File is required');
        } else if (campaign.wrong_input_file.enabled && !campaign.wrong_input_file.selected) {
          valid = false;
          toast.error('Wrong Input File is required');
        } else if (campaign.thanks_file.enabled && !campaign.thanks_file.selected) {
          valid = false;
          toast.error('Thanks File is required');
        }
      }
      if (campaign.template_id == 2) {
        if (campaign.enableCallPatchAdditionalSettings) {
          if (!campaign.no_agent_file?.selected) {
            valid = false;
            toast.error('No Agent File is required');
          }
          if (!campaign.menu_voice_file) {
            valid = false;
            toast.error('Outbound Campaign Menu Voice File is required');
          } else if (!campaign.menu_wait_time && campaign.menu_wait_time !== 0) {
            valid = false;
            toast.error('Menu Wait Time is required');
          } else if (!campaign.re_prompt && campaign.re_prompt !== 0) {
            valid = false;
            toast.error('Re-Prompt is required');
          } else if (campaign.welcome_file.enabled && !campaign.welcome_file.selected) {
            valid = false;
            toast.error('Welcome File is require');
          } else if (campaign.no_input_file.enabled && !campaign.no_input_file.selected) {
            valid = false;
            toast.error('No Input File is required');
          } else if (campaign.wrong_input_file.enabled && !campaign.wrong_input_file.selected) {
            valid = false;
            toast.error('Wrong Input File is required');
          } else if (campaign.thanks_file.enabled && !campaign.thanks_file.selected) {
            valid = false;
            toast.error('Thanks File is required');
          }
        }
        if (campaign.assignedQueues.length === 0) {
          valid = false;
          toast.error('Assigned Call Group is required');
        } else if (!campaign.enableCallPatchAdditionalSettings && campaign.assignedQueues.length > 1) {
          valid = false;
          toast.error('Only one call group is allowed when Additional Settings is disabled');
        } else if (
          campaign.enableCallPatchAdditionalSettings &&
          campaign.assignedQueues.some((q) => q.dtmf === undefined || q.dtmf === null || q.dtmf === '')
        ) {
          valid = false;
          toast.error('Each assigned call group must have a DTMF digit');
        }
      }
    }
    if (isPowerDialerCampaign(campaign)) {
      if (campaign.assignedQueues.length === 0) {
        valid = false;
        toast.error('Call Group is required');
      } else if (usesCallPatchSingleCallGroup(campaign) && campaign.assignedQueues.length > 1) {
        valid = false;
        toast.error('Only one call group is allowed when Additional Settings is disabled');
      } else if (
        usesCallGroupDtmf(campaign) &&
        campaign.assignedQueues.some((q) => q.dtmf === undefined || q.dtmf === null || q.dtmf === '')
      ) {
        valid = false;
        toast.error('Each call group must have a DTMF digit');
      }
      if (campaign.enableCallPatchAdditionalSettings) {
        if (!campaign.no_agent_file?.selected) {
          valid = false;
          toast.error('No Agent File is required');
        }
        if (!campaign.menu_voice_file) {
          valid = false;
          toast.error('Outbound Campaign Menu Voice File is required');
        } else if (!campaign.menu_wait_time && campaign.menu_wait_time !== 0) {
          valid = false;
          toast.error('Menu Wait Time is required');
        } else if (!campaign.re_prompt && campaign.re_prompt !== 0) {
          valid = false;
          toast.error('Re-Prompt is required');
        }
      }
    }
    if (campaign.sendSmsOnSuccess && !campaign.successSmsWebhook) {
      valid = false;
      toast.error('Please select a Success SMS Webhook.');
    }
    if (campaign.sendSmsOnFail && !campaign.failSmsWebhook) {
      valid = false;
      toast.error('Please select a Fail SMS Webhook.');
    }
    if (campaign.enableWebhook && !campaign.webhook) {
      valid = false;
      toast.error('Please select a Webhook.');
    }
    if (campaign.enableSmsOnDtmf && !campaign.smsOnDtmf) {
      valid = false;
      toast.error('Please select a DTMF SMS Webhook.');
    }
    if (campaign.enableSmsOnPatchSuccess && !campaign.smsOnPatchSuccess) {
      valid = false;
      toast.error('Please select a Patch Success SMS Webhook.');
    }
    if (campaign.enableSmsOnPatchFailed && !campaign.smsOnPatchFailed) {
      valid = false;
      toast.error('Please select a Patch Fail SMS Webhook.');
    }
    if (campaign.template_id == 3 && !campaign.ivr) {
      valid = false;
      toast.error('Please select IVR');
    }
    if (campaign.startHour === campaign.endHour) {
      valid = false;
      toast.error('Please select different outbound campaign hours');
    }
    if (isEdit && campaign.end_date && campaign.end_date < getToday()) {
      toast.error(END_DATE_IN_PAST_MESSAGE);
      return false;
    }
    if (startDateChanged && campaign.start_date && campaign.start_date < getToday()) {
      valid = false;
      toast.error(START_DATE_IN_PAST_MESSAGE);
      return false;
    }
    if (
      startMinApplies(campaign) &&
      campaign.startHour &&
      /^\d{2}:\d{2}$/.test(campaign.startHour)
    ) {
      let minTime = getMinStartTime(minStartTimeMode(campaign.strict_timings));
      if (isPromotionalAccount(user)) minTime = clampToPromotionalWindow(minTime);
      const enteredMin = timeToMinutes(campaign.startHour);
      const minAllowedMin = timeToMinutes(minTime);
      if (enteredMin < minAllowedMin) {
        valid = false;
        toast.error(startTimeTooEarlyMessage(minTime));
        return false;
      }
    }
    if (isPromotionalAccount(user)) {
      const windowStart = timeToMinutes(AUTO_DIALER_SCHEDULE_START);
      const windowEnd = timeToMinutes(AUTO_DIALER_SCHEDULE_END);
      const start = timeToMinutes(campaign.startHour);
      const end = timeToMinutes(campaign.endHour);
      if (start == null || end == null || start < windowStart || start > windowEnd || end < windowStart || end > windowEnd) {
        valid = false;
        toast.error('Promotional accounts can only schedule campaigns between 09:00 and 21:00.');
        return false;
      }
    }
    const start = timeToMinutes(campaign.startHour);
    const end = timeToMinutes(campaign.endHour);
    if (campaign.start_date === campaign.end_date) {
      if (end <= start) {
        valid = false;
        toast.error('End Outbound Campaign Hour cannot be before Start Outbound Campaign Hour');
        return false;
      }
    }
    if (endsInThePast(campaign)) {
      toast.error(END_TIME_IN_PAST_MESSAGE);
      return false;
    }
    if (valid) {
      const pauseError = pauseSlotsError();
      if (pauseError) {
        valid = false;
        toast.error(pauseError);
      }
    }
    return valid;
  }

  const handleTemplateChange = (e) => {
    const newTemplateId = e.target.value;
    setCampaign((prev) => ({
      ...prev,
      template_id: newTemplateId,
      voice_file: '',
      voice_file_language: '',
      menu_voice_file: '',
      menu_voice_file_language: '',
      dtmf: '',
      menu_wait_time: '',
      re_prompt: '',
      welcome_file: { enabled: false, selected: '', language: '' },
      no_input_file: { enabled: false, selected: '', language: '' },
      wrong_input_file: { enabled: false, selected: '', language: '' },
      thanks_file: { enabled: false, selected: '', language: '' },
      no_agent_file: { enabled: false, selected: '', language: '' },
      call_group: [],
      agentAssignment: '',
      selectedQueue: '',
      selectedDtmf: '',
      assignedQueues: [],
      enableCallPatchAdditionalSettings: false,
      ivr: '',
      contactUploadMethod: '',
      manualContactNumbers: '',
      existingVoiceBaseId: '',
      base_id: null,
      selectedAgentsForQueue: [],
      startHour: AUTO_DIALER_SCHEDULE_START,
      endHour: AUTO_DIALER_SCHEDULE_END,
      scriptOption: newTemplateId === '0' || newTemplateId === '1' ? 'none' : prev.scriptOption,
    }));
    setContactUploadFile(null);
    setFieldErrors({});
  };

  useEffect(() => {
    if (sourceCampId) {
      const url = API_VOICE_CAMPAIGNS_GET_ONE.replace(':id', sourceCampId);
      getApiCall(url, {})
        .then(async (resp) => {
          if (isEdit && !canEditCampaignNow(resp)) {
            toast.error(
              isReadyEditLocked(resp)
                ? READY_EDIT_LOCKED_MESSAGE
                : 'Only Ready or Paused campaigns can be edited. Pause the campaign first.',
            );
            navigate(`/${ROUTES.CAMPAIGNS.LIST}`);
            return;
          }
          if (resp.campaign_type !== null && resp.campaign_type !== undefined) {
            resp.campaign_type = String(resp.campaign_type);
          }
          if (resp.template_id !== null && resp.template_id !== undefined) {
            resp.template_id = String(resp.template_id);
          }
          if (resp.time_between_call) resp.timeBetweenCalls = resp.time_between_call;
          if (isEdit) {
            setOriginalName(resp.campaign_name || '');
            setOriginalStartDate(resp.start_date || '');
            setEditStatus(Number(resp.campaign_status));
          }
          else if (resp.campaign_name) resp.campaign_name = '';

          const schedule = resp.voice_campaigns_schedule?.[0] || {};

          const {
            campaign_id, user_id, schedule_date, campaign_status,
            start_hours, end_hours, script_id, dtmf_count, dtmf1_count, dtmf2_count,
            is_recording, prompt_duration, sms_text, sms_count, trunk, context,
            total_nos, nos_processed, connected_calls, agents, is_retry,
            retry_interval, auto_retry_count, time_between_call, invalid_retry,
            unit_rate, channels, pulses, pulse_duration, parent_pulse_duration,
            s_parent_pulse_duration, base_id, parent_id, s_parent_id, country_code,
            dnd_count, error_desc, target_type, type_run, first_call, is_deleted,
            created_at, updated_at, ...restResp
          } = resp;

          const mappedCampaign: any = {
            ...restResp,
            sms_text: resp.sms_text || '',
            startHour: schedule.start_time || '',
            endHour: schedule.end_time || '',
            strict_timings: isStrictTimings(resp.strict_timings) ? 1 : 0,
            weekDays: Array.isArray(schedule.week_days) ? schedule.week_days : [],
            pause_slots: Array.isArray(schedule.pause_slots)
              ? schedule.pause_slots
                  .filter((s) => s?.start_time && s?.end_time)
                  .map((s) => ({ name: s.name || '', start_time: s.start_time, end_time: s.end_time }))
              : [],
            start_date: resp.start_date || '',
            end_date: resp.end_date || '',
            max_retries: resp.max_retries === 0 || resp.max_retries ? resp.max_retries : '',
            enableMaxRetries: Number(is_retry) === 1 || Number(resp.max_retries) > 0,
            retryInterval: resp.retry_interval === 0 || resp.retry_interval ? resp.retry_interval : '',
            retry_mode:
              resp.retry_mode === RETRY_MODE_AFTER_COMPLETION ? RETRY_MODE_AFTER_COMPLETION : RETRY_MODE_ON_FAILURE,
            removeDuplicates: true,
            first_call: first_call || 'customer',
            enableCallPatchAdditionalSettings: Number(resp.enable_call_patch_additional_settings) === 1,
            timeBetweenCalls: resp.time_between_call || '2',
            // VMN / TOLL FREE are incoming-only — don't carry them into the clone.
            did: (resp.campaign_numbers || [])
              .filter((n) => ![NUMBERS_TYPES_VMN, NUMBERS_TYPES_TOLFREE].includes(String(n.number_details_data?.number_type || '').toUpperCase()))
              .map((n) => n.number_details_data?.id)
              .filter(Boolean),
            call_group: (resp.campaign_contact_group || []).map((g) => String(g.contact_group_id)),
            locations: resp.campaignLocations?.map((loc) => ({ id: loc.location_id, location_name: loc.location_name })),
            successSmsWebhook: '',
            failSmsWebhook: '',
            webhook: '',
            scriptOption: 'none',
            selectedScript: '',
            voice_file: '',
            voice_file_language: '',
            menu_voice_file: '',
            menu_voice_file_language: '',
            duration: '',
            assignedQueues: [],
            selectedDispositionForm: resp.disposition_form_id ?? null,
            // Duplicate keeps the source ratio only if this user's ACL still allows it.
            pacing_ratio: allowedPacingRatios.includes(String(resp.pacing_ratio || '')) ? String(resp.pacing_ratio) : defaultPacingRatio,
            contactUploadMethod: isEdit && base_id ? 'existing' : (resp.campaign_contact_group || []).length ? 'contact_group' : '',
            existingVoiceBaseId: isEdit && base_id ? String(base_id) : '',
            base_id: isEdit ? base_id ?? null : null,
            manualContactNumbers: '',
            welcome_file: { enabled: false, selected: '', language: '' },
            no_input_file: { enabled: false, selected: '', language: '' },
            wrong_input_file: { enabled: false, selected: '', language: '' },
            thanks_file: { enabled: false, selected: '', language: '' },
            no_agent_file: { enabled: false, selected: '', language: '' },
          };

          if (resp.campaign_type === '4' && resp.pacing_ratio && mappedCampaign.pacing_ratio !== String(resp.pacing_ratio)) {
            toast.info(`Pacing ratio ${resp.pacing_ratio} is not allowed on your account; please pick an allowed ratio.`);
          }

          if (resp.template_id == 0 || resp.template_id == 1) {
            mappedCampaign.selectedQueue = resp?.voice_campaigns_call_group_details[0]?.call_group_id;
            mappedCampaign.agentAssignment = 'queue';
          }

          if (Array.isArray(resp.voice_campaign_webhook_details)) {
            const connectedWebhook = resp.voice_campaign_webhook_details.find((w) => w.event === 'CALL_CONNECTED');
            const failedWebhook = resp.voice_campaign_webhook_details.find((w) => w.event === 'CALL_FAILED');
            const generalWebhook = resp.voice_campaign_webhook_details.find(
              (w) => w.event === 'CONNECTED_CALLS' || w.event === 'HANGUP' || w.event === 'DTMF',
            );
            const dtmfWebhook = resp.voice_campaign_webhook_details.find((w) => w.event === 'VALID_DTMF');
            const patchFailedWebhook = resp.voice_campaign_webhook_details.find((w) => w.event === 'PATCH_FAILED');
            const patchConnectedWebhook = resp.voice_campaign_webhook_details.find((w) => w.event === 'PATCH_CONNECTED');

            mappedCampaign.successSmsWebhook = connectedWebhook?.sms_webhook_id || '';
            mappedCampaign.failSmsWebhook = failedWebhook?.sms_webhook_id || '';
            mappedCampaign.webhook = generalWebhook?.webhook_id || '';
            mappedCampaign.duration = connectedWebhook?.duration || '';
            mappedCampaign.smsOnDtmf = dtmfWebhook?.sms_webhook_id || '';
            mappedCampaign.smsOnPatchSuccess = patchConnectedWebhook?.sms_webhook_id || '';
            mappedCampaign.smsOnPatchFailed = patchFailedWebhook?.sms_webhook_id || '';
          }

          mappedCampaign.sendSmsOnSuccess = !!mappedCampaign.successSmsWebhook;
          mappedCampaign.sendSmsOnFail = !!mappedCampaign.failSmsWebhook;
          mappedCampaign.enableWebhook = !!mappedCampaign.webhook;
          mappedCampaign.enableSmsOnDtmf = !!mappedCampaign.smsOnDtmf;
          mappedCampaign.enableSmsOnPatchSuccess = !!mappedCampaign.smsOnPatchSuccess;
          mappedCampaign.enableSmsOnPatchFailed = !!mappedCampaign.smsOnPatchFailed;

          if (resp.voice_campaigns_scripts_details) {
            mappedCampaign.scriptOption = 'existing';
            mappedCampaign.selectedScript = resp.voice_campaigns_scripts_details.id;
          } else {
            mappedCampaign.scriptOption = 'none';
          }

          if (resp.template_id == '0' && resp.campaigns?.length > 0) {
            mappedCampaign.voice_file = resp.campaigns[0]?.value || '';
            mappedCampaign.voice_file_language = resp.campaigns[0]?.language || '';
          } else if (['1', '2'].includes(resp.template_id) && resp.campaigns?.length > 0) {
            const menuCampaign = resp.campaigns.find((c) => c.name === 'menu');
            mappedCampaign.menu_voice_file = menuCampaign ? menuCampaign.value : '';
            mappedCampaign.menu_voice_file_language = menuCampaign ? menuCampaign.language || '' : '';
            if (String(resp.template_id) === '2') {
              mappedCampaign.enableCallPatchAdditionalSettings = Number(resp.enable_call_patch_additional_settings) === 1;
              if (isAutoDialerCampaign(mappedCampaign) && isAgentFirstValue(mappedCampaign.first_call)) {
                mappedCampaign.enableCallPatchAdditionalSettings = false;
                mappedCampaign.menu_voice_file = '';
                mappedCampaign.menu_voice_file_language = '';
              }
            }
          }

          if ((resp.template_id === '1' || resp.template_id === '2') && resp.campaigns?.length > 0) {
            const fileMappings: any = {
              welcome_file: 'welcome',
              no_input_file: 'noinput',
              wrong_input_file: 'wronginput',
              thanks_file: 'thanks',
              no_agent_file: 'noagent',
            };
            Object.entries(fileMappings).forEach(([stateKey, campaignName]) => {
              const match = resp.campaigns.find((c) => c.name === campaignName);
              mappedCampaign[stateKey] = {
                enabled: !!match,
                selected: match ? match.value : '',
                language: match ? match.language || '' : '',
              };
            });
            if (String(resp.template_id) === '2' && !mappedCampaign.enableCallPatchAdditionalSettings) {
              ['welcome_file', 'no_input_file', 'wrong_input_file', 'thanks_file', 'no_agent_file'].forEach((key) => {
                mappedCampaign[key] = { enabled: false, selected: '', language: '' };
              });
              mappedCampaign.menu_wait_time = '';
              mappedCampaign.re_prompt = '';
            }
          }

          if (
            String(resp.template_id) === CALL_PATCH_TEMPLATE_VALUE &&
            resp.voice_campaigns_call_group_details?.length > 0
          ) {
            mappedCampaign.agentAssignment = 'queue';
            mappedCampaign.assignedQueues = resp.voice_campaigns_call_group_details.map((item) => ({
              queueId: item.call_group_id,
              queueName: item.agent_group_campaign_details?.group_name || '',
              dtmf: item.dtmf || '',
            }));
            if (!mappedCampaign.enableCallPatchAdditionalSettings) {
              const first = mappedCampaign.assignedQueues[0];
              mappedCampaign.assignedQueues = [{ ...first, dtmf: null }];
            }
            mappedCampaign.selectedQueue = mappedCampaign.assignedQueues[0]?.queueId || '';
          } else if (resp?.voice_campaigns_call_group_details?.length > 0) {
            mappedCampaign.agentAssignment = 'queue';
            const details = resp.voice_campaigns_call_group_details;
            mappedCampaign.assignedQueues = details.map((item) => ({
              queueId: item.call_group_id,
              queueName: item.agent_group_campaign_details?.group_name || '',
              dtmf: item.dtmf || '',
            }));
            mappedCampaign.selectedQueue = details[0]?.call_group_id || '';
          }

          if (isEdit && isClick2CallCampaign(mappedCampaign) && mappedCampaign.assignedQueues.length > 0) {
            const onCampaign = new Set((Array.isArray(agents) ? agents : []).map(String));
            mappedCampaign.assignedQueues = await Promise.all(
              mappedCampaign.assignedQueues.map(async (q) => {
                const list = await getApiCall(API_AGENT_GROUP_ASSIGNED_AGENTS.replace(':id', q.queueId), {}).catch(() => []);
                const picked = (Array.isArray(list) ? list : []).filter((a) => onCampaign.has(String(a.agent_id ?? a.id)));
                return {
                  ...q,
                  agentIds: picked.map((a) => a.agent_id ?? a.id),
                  agentLabels: picked.map((a) => a.agent_name || a.name || a.username || `Agent ${a.agent_id ?? a.id}`),
                };
              }),
            );
            mappedCampaign.selectedQueue = '';
          }

          if (
            !isEdit &&
            isAutoDialerCampaign(mappedCampaign) &&
            isAgentFirstValue(mappedCampaign.first_call) &&
            String(mappedCampaign.template_id) !== CALL_PATCH_TEMPLATE_VALUE
          ) {
            mappedCampaign.template_id = CALL_PATCH_TEMPLATE_VALUE;
            mappedCampaign.enableCallPatchAdditionalSettings = false;
            mappedCampaign.voice_file = '';
            mappedCampaign.voice_file_language = '';
            mappedCampaign.dtmf = '';
            mappedCampaign.ivr = '';
          }

          setCampaign((prev) => ({ ...prev, ...mappedCampaign }));
          setUnlockedUpToIndex(buildStepFlow(mappedCampaign, isEdit).length - 1);
        })
        .catch((error) => {
          console.error('❌ Failed to fetch source campaign:', error);
        });
    }
  }, [sourceCampId]);

  // Just the Dialer Type dropdown — First Call Connect lives in its own
  // helper below so the merged Basics step can position it independently.
  const renderDialer = () => (
    <div>
      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
          <Req />
          Select Dialer Type
        </Form.Label>
        <Form.Select
          name="campaign_type"
          value={campaign.campaign_type}
          onChange={handleChange}
          disabled={isEdit}
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '6px',
            border: fieldErrors.campaign_type ? '1px solid red' : '1px solid #ced4da',
          }}
        >
          {dialerTypeOptions.length !== 1 && (
            <option value="">
              {campaignOptionsLoaded ? 'Choose a Dialer Type' : 'Loading dialer types...'}
            </option>
          )}
          {dialerTypeOptions.map((option) => (
            <option key={option.id} value={String(option.value)}>
              {option.name}
            </option>
          ))}
        </Form.Select>
        <FieldError message={fieldErrors.campaign_type} />
        <FieldError message={campaignOptionsError} />
      </Form.Group>

      {/* On legacy 'dialer' key still show First Call Connect inline. */}
      {currentStepKey === 'dialer' && isAutoDialerCampaign(campaign) && renderFirstCall()}
    </div>
  );

  const renderFirstCall = () => (
    <Form.Group style={{ marginBottom: '1.5rem' }}>
      <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
        <Req />
        First Call Connect
      </Form.Label>
      <Form.Select
        name="first_call"
        value={campaign.first_call || 'customer'}
        onChange={handleChange}
        style={{
          padding: '0.5rem 0.75rem',
          borderRadius: '6px',
          border: fieldErrors.first_call ? '1px solid red' : '1px solid #ced4da',
        }}
      >
        <option value="agent" disabled={isEdit && String(campaign.template_id) !== CALL_PATCH_TEMPLATE_VALUE}>
          Agent First
        </option>
        <option value="customer">Customer First</option>
      </Form.Select>
      <FieldError message={fieldErrors.first_call} />
    </Form.Group>
  );

  const renderTemplate = () => (
    <div>
      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
          <Req />
          Select Template
        </Form.Label>
        <Form.Select
          name="template_id"
          value={campaign.template_id}
          onChange={handleTemplateChange}
          disabled={isEdit}
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '6px',
            border: fieldErrors.template_id ? '1px solid red' : '1px solid #ced4da',
          }}
        >
          {allowedTemplateOptions.length !== 1 && (
            <option value="">
              {campaignOptionsLoaded ? 'Choose a template' : 'Loading templates...'}
            </option>
          )}
          {allowedTemplateOptions.map((option) => (
            <option key={option.id} value={String(option.value)}>
              {option.name}
            </option>
          ))}
        </Form.Select>
        <FieldError message={fieldErrors.template_id} />
        <FieldError message={campaignOptionsError} />
      </Form.Group>
    </div>
  );

  const renderStep = () => {
    switch (currentStepKey) {
      // Merged step: three-column row for the top selects — Dialer Type,
      // First Call Connect, Template (both auto-dialer only). The rest of General (Campaign Name
      // and its conditional siblings) flows underneath.
      case 'basics':
        return (
          <div>
            <div className="campaigns-basics-topgrid">
              {renderDialer()}
              {isAutoDialerCampaign(campaign) && renderFirstCall()}
              {isAutoDialerCampaign(campaign) && renderTemplate()}
            </div>
            {renderGeneral()}
          </div>
        );

      // Legacy 'type' key — kept in case any deeplink lands on it.
      case 'type':
        return (
          <>
            {renderDialer()}
            {isAutoDialerCampaign(campaign) && renderTemplate()}
          </>
        );

      // Merged step: settings + schedule side-by-side on wide screens.
      // Settings column carries Record calls / DID / Time between calls
      // / Retry; Schedule column carries Duration / Week days / Hours.
      case 'setup':
        return (
          <div className="campaigns-setup-grid">
            <div className="campaigns-setup-col">
              <div className="campaigns-setup-heading">Settings</div>
              {renderSettings()}
            </div>
            <div className="campaigns-setup-col">
              <div className="campaigns-setup-heading">Schedule</div>
              {renderSchedule()}
            </div>
          </div>
        );

      // Legacy keys — kept so any old bookmark/deeplink still resolves.
      case 'dialer':
        return renderDialer();
      case 'template':
        return renderTemplate();

      case 'general':
        return renderGeneral();

      case 'upload_contacts':
        return renderUploadContacts();

      case 'bases':
        return (
          <div>
            <p className="mb-3 text-xs text-muted-foreground">Existing bases stay as they are. Added bases are saved right away.</p>
            <CampaignBasesSection campaignId={edit_camp_id} allowAdd allowStatusChange />
          </div>
        );

      case 'script':
        return renderScript();

      case 'settings':
        return renderSettings();

      case 'schedule':
        return renderSchedule();

      case 'sms':
        return renderSms();

      default:
        return null;
    }
  };

  const renderGeneral = () => (
    <div>
      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
          <Req />
          Outbound Campaign name
        </Form.Label>
        <Form.Control
          type="text"
          name="campaign_name"
          value={campaign.campaign_name}
          onChange={handleChange}
          placeholder="Create a suitable name for the outbound campaign"
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '6px',
            border:
              fieldErrors.campaign_name || campaignNameStatus === 'taken'
                ? '1px solid red'
                : '1px solid #ced4da',
            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
          }}
        />
        {error && <small style={{ color: 'red', marginTop: '4px' }}>{error}</small>}
        {fieldErrors.campaign_name ? (
          <FieldError message={fieldErrors.campaign_name} />
        ) : (
          <AvailabilityHint status={campaignNameStatus} takenMessage={CAMPAIGN_NAME_EXISTS_MSG} />
        )}
      </Form.Group>

      {isPowerDialerCampaign(campaign) && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
            <Req />
            Pacing Ratio
          </Form.Label>
          <Form.Select
            name="pacing_ratio"
            value={campaign.pacing_ratio || ''}
            onChange={handleChange}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: fieldErrors.pacing_ratio ? '1px solid red' : '1px solid #ced4da',
            }}
          >
            {!allowedPacingRatios.includes(String(campaign.pacing_ratio || '')) && (
              <option value="" disabled>
                {allowedPacingRatios.length ? 'Select pacing ratio' : 'No pacing ratio allowed'}
              </option>
            )}
            {allowedPacingRatios.map((ratio) => (
              <option key={ratio} value={ratio}>
                {ratio}
              </option>
            ))}
          </Form.Select>
          <FieldError message={fieldErrors.pacing_ratio} />
          {(() => {
            const [agents, customers] = String(campaign.pacing_ratio || '1:1').split(':').map(Number);
            const perAgent = customers / agents || 1;
            return (
              <div
                role="note"
                style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem 1rem',
                  borderRadius: '6px',
                  background: '#fff8e13f',
                  border: '1px solid #ffc107',
                  borderLeft: '4px solid #ffc107',
                  color: '#664d03',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                }}
              >
                <strong>Note: channel licences required</strong>
                <div>
                  Per live agent at <strong>{campaign.pacing_ratio || '—'}</strong>: WebRTC <strong>{perAgent}</strong>,
                  Mobile <strong>{perAgent + 1}</strong> (+1 for agent&apos;s mobile) for each call. Fewer channels = fewer
                  calls.
                </div>
              </div>
            );
          })()}
        </Form.Group>
      )}

      {usesSimpleIvrTemplate(campaign) && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
            <Req />
            Upload Voice File
          </Form.Label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Form.Select
            name="voice_file"
            value={campaign.voice_file}
            onChange={handlePromptFieldChange('voice_file', 'voice_file_language', welcomeFileList)}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: fieldErrors.voice_file ? '1px solid red' : '1px solid #ced4da',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
            }}
          >
            <option>Select...</option>
            {welcomeFileList.map((file) => (
              <option key={file.id} value={file.id}>
                {file.prompt_name}
              </option>
            ))}
          </Form.Select>
          {renderPromptPlayButton(campaign.voice_file, campaign.voice_file_language, welcomeFileList)}
          </div>
          <FieldError message={fieldErrors.voice_file} />
          {renderPromptLanguageSelect('voice_file', 'voice_file_language', welcomeFileList, 'voice_file_language')}
        </Form.Group>
      )}

      {supportsCallPatchAdditionalSettings(campaign) && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Check
            type="checkbox"
            id="enableCallPatchAdditionalSettings"
            label="Additional Settings"
            name="enableCallPatchAdditionalSettings"
            checked={!!campaign.enableCallPatchAdditionalSettings}
            onChange={handleChange}
          />
        </Form.Group>
      )}

      {(campaign.template_id == 1 ||
        (supportsCallPatchAdditionalSettings(campaign) && campaign.enableCallPatchAdditionalSettings)) && (
        <>
          <Form.Group style={{ marginBottom: '1.5rem' }}>
            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              Upload Menu Voice File
            </Form.Label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Form.Select
              name="menu_voice_file"
              value={campaign.menu_voice_file}
              onChange={handlePromptFieldChange('menu_voice_file', 'menu_voice_file_language', menuFileList)}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: fieldErrors.menu_voice_file ? '1px solid red' : '1px solid #ced4da',
              }}
            >
              <option>Select Menu Voice File</option>
              {menuFileList.map((file) => (
                <option key={file.id} value={file.id}>
                  {file.prompt_name}
                </option>
              ))}
            </Form.Select>
            {renderPromptPlayButton(campaign.menu_voice_file, campaign.menu_voice_file_language, menuFileList)}
            </div>
            <FieldError message={fieldErrors.menu_voice_file} />
            {renderPromptLanguageSelect('menu_voice_file', 'menu_voice_file_language', menuFileList, 'menu_voice_file_language')}
          </Form.Group>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
            {campaign.template_id == 1 && (
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: 500, color: '#495057' }}>
                  <Req />
                  DTMF
                </Form.Label>
                <Select
                  isMulti
                  name="dtmf"
                  options={[...Array(10).keys()].map((num) => ({ value: num.toString(), label: num.toString() }))}
                  value={
                    campaign.dtmf
                      ? campaign.dtmf.split(',').map((val) => ({ value: val.trim(), label: val.trim() }))
                      : []
                  }
                  onChange={(selected: any) => {
                    setCampaign((prev) => ({ ...prev, dtmf: selected.map((opt) => opt.value).join(',') }));
                    if (selected.length > 0) setFieldErrors((prev) => ({ ...prev, dtmf: '' }));
                  }}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderRadius: '6px',
                      border: fieldErrors.dtmf ? '1px solid red' : '1px solid #ced4da',
                      padding: '2px',
                    }),
                    menu: (base) => ({ ...base, zIndex: 5 }),
                  }}
                  menuPlacement="auto"
                  className="theme-react-select"
                  classNamePrefix="theme-react-select"
                />
                <FieldError message={fieldErrors.dtmf} />
              </Form.Group>
            )}

            <Form.Group style={{ flex: 1 }}>
              <Form.Label style={{ fontWeight: 500, color: '#495057' }}>
                <Req />
                Menu Wait Time
              </Form.Label>
              <Form.Select
                name="menu_wait_time"
                value={campaign.menu_wait_time}
                onChange={handleChange}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  border: fieldErrors.menu_wait_time ? '1px solid red' : '1px solid #ced4da',
                }}
              >
                <option value="">Select Menu Wait Time</option>
                {[...Array(10).keys()].map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </Form.Select>
              <FieldError message={fieldErrors.menu_wait_time} />
            </Form.Group>

            <Form.Group style={{ flex: 1 }}>
              <Form.Label style={{ fontWeight: 500, color: '#495057' }}>
                <Req />
                Re-Prompt
              </Form.Label>
              <Form.Select
                name="re_prompt"
                value={campaign.re_prompt}
                onChange={handleChange}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  border: fieldErrors.re_prompt ? '1px solid red' : '1px solid #ced4da',
                }}
              >
                <option value="">Select Re-Prompt</option>
                {[...Array(4).keys()].map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </Form.Select>
              <FieldError message={fieldErrors.re_prompt} />
            </Form.Group>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              { name: 'welcome_file', label: 'Welcome File', list: welcomeFileList },
              { name: 'no_input_file', label: 'No Input File', list: noInputFileList },
              { name: 'wrong_input_file', label: 'Wrong Input File', list: wrongInputFileList },
              { name: 'thanks_file', label: 'Thanks File', list: thanksFileList },
            ].map((item) => (
              <div key={item.name} style={{ flex: 1 }}>
                <Form.Check
                  type="checkbox"
                  label={item.label}
                  checked={campaign[item.name]?.enabled || false}
                  onChange={(e) =>
                    handleChange({ target: { name: item.name, value: { enabled: e.target.checked, selected: '' } } } as any)
                  }
                />
                {campaign[item.name]?.enabled && (
                  <>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                    <Form.Select
                      value={campaign[item.name]?.selected || ''}
                      onChange={handleNestedPromptFieldChange(item.name, item.list)}
                      style={{
                        border: fieldErrors[item.name] ? '1px solid red' : '1px solid #ced4da',
                      }}
                    >
                      <option value="">Select file {item.label}</option>
                      {item.list.map((file) => (
                        <option key={file.id} value={file.id}>
                          {file.prompt_name}
                        </option>
                      ))}
                    </Form.Select>
                    {renderPromptPlayButton(campaign[item.name]?.selected, campaign[item.name]?.language, item.list)}
                    </div>
                    <FieldError message={fieldErrors[item.name]} />
                    {renderNestedPromptLanguageSelect(item.name, item.list)}
                  </>
                )}
              </div>
            ))}
          </div>

          {campaign.template_id == 2 && (
            <Form.Group style={{ marginBottom: '1.5rem' }}>
              <Form.Check
                type="checkbox"
                id="no_agent_file_always"
                label={
                  <>
                    <span style={{ color: 'red', marginRight: '4px' }}>*</span>
                    No Agent File
                  </>
                }
                checked={campaign.no_agent_file?.enabled || false}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: 'no_agent_file',
                      value: { enabled: e.target.checked, selected: '', language: '' },
                    },
                  } as any)
                }
              />
              {campaign.no_agent_file?.enabled && (
                <>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  <Form.Select
                    value={campaign.no_agent_file?.selected || ''}
                    onChange={handleNestedPromptFieldChange('no_agent_file', noAgentFileList)}
                    style={{
                      maxWidth: '360px',
                      border: fieldErrors.no_agent_file ? '1px solid red' : '1px solid #ced4da',
                    }}
                  >
                    <option value="">Select file No Agent File</option>
                    {noAgentFileList.map((file) => (
                      <option key={file.id} value={file.id}>
                        {file.prompt_name}
                      </option>
                    ))}
                  </Form.Select>
                  {renderPromptPlayButton(campaign.no_agent_file?.selected, campaign.no_agent_file?.language, noAgentFileList)}
                  </div>
                  <FieldError message={fieldErrors.no_agent_file} />
                  {renderNestedPromptLanguageSelect('no_agent_file', noAgentFileList)}
                </>
              )}
              {!campaign.no_agent_file?.enabled && fieldErrors.no_agent_file && (
                <FieldError message={fieldErrors.no_agent_file} />
              )}
            </Form.Group>
          )}
        </>
      )}

      {!isEdit && !shouldHideContactGroupOnGeneral(campaign) && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
            <Req />
            Select contact group
          </Form.Label>
          <Select
            isMulti
            name="call_group"
            options={contactGroupList.map((group) => ({
              value: group.id,
              label: contactGroupLabel(group),
              isDisabled: !Number(group.member_count),
            }))}
            value={contactGroupList
              .filter((group) => campaign.call_group?.includes(group.id))
              .map((group) => ({ value: group.id, label: contactGroupLabel(group) }))}
            onChange={(selected: any) => {
              setCampaign((prev) => ({ ...prev, call_group: selected.map((opt) => opt.value) }));
              if (selected.length > 0) setFieldErrors((prev) => ({ ...prev, call_group: '' }));
            }}
            styles={{
              control: (provided) => ({
                ...provided,
                borderRadius: '6px',
                border: fieldErrors.call_group ? '1px solid red' : '1px solid #ced4da',
                padding: '2px',
              }),
              menu: (base) => ({ ...base, zIndex: 5 }),
            }}
            menuPlacement="auto"
            className="theme-react-select"
            classNamePrefix="theme-react-select"
          />
          <FieldError message={fieldErrors.call_group} />
        </Form.Group>
      )}

      {!usesContactUpload(campaign) && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Label
            style={{
              fontWeight: 500,
              color: '#495057',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            Disposition Form
          </Form.Label>
          <Form.Select
            name="selectedDispositionForm"
            value={campaign.selectedDispositionForm || ''}
            onChange={(e) => {
              const value = e.target.value;
              setCampaign((prev) => ({ ...prev, selectedDispositionForm: value ? value : null }));
            }}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: '1px solid #ced4da',
            }}
          >
            <option value="">None (use default form)</option>
            {Array.isArray(filteredDispositionForms) &&
              filteredDispositionForms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.name} ({formatDispositionCallDirectionLabel(form.call_direction)})
                </option>
              ))}
          </Form.Select>
        </Form.Group>
      )}

      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <div>{renderCallGroupUI()}</div>
      </Form.Group>

      {campaign.template_id == 3 && campaign.campaign_type == 2 && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
            <Req />
            Select IVR
            <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>
              (Only completed IVRs are shown)
            </span>
          </Form.Label>
          <Form.Select
            name="ivr"
            value={campaign.ivr}
            onChange={handleChange}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: fieldErrors.ivr ? '1px solid red' : '1px solid #ced4da',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
            }}
          >
            <option>Select...</option>
            {ivrList
              .filter((ivr) => Number(ivr.status) === 1)
              .map((ivr) => (
                <option key={ivr.id} value={ivr.id}>
                  {ivr.flow_name}
                </option>
              ))}
          </Form.Select>
          <FieldError message={fieldErrors.ivr} />
        </Form.Group>
      )}
    </div>
  );

  const renderCallGroupUI = () => (
    <>
      {shouldShowCallGroupCheckboxForTemplate(campaign) && (
        <>
          {isCallGroupForced(campaign) ? (
            <div style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
              <span style={{ color: 'red', marginRight: '4px' }}>*</span>
              Select Call Group
            </div>
          ) : (
            <Form.Check
              type="checkbox"
              id="selectQueue"
              label={
                <>
                  {campaign.template_id == 1 && <span style={{ color: 'red', marginRight: '4px' }}>*</span>}
                  Select Call Group
                </>
              }
              name="agentAssignment"
              value="queue"
              checked={campaign.agentAssignment === 'queue'}
              onChange={() =>
                setCampaign((prev) => ({
                  ...prev,
                  agentAssignment: prev.agentAssignment === 'queue' ? '' : 'queue',
                }))
              }
              style={{ marginBottom: '0.75rem' }}
            />
          )}
          {campaign.agentAssignment === 'queue' && (
            <Form.Select
              style={{ marginTop: '0.5rem', marginBottom: '1rem' }}
              name="selectedQueue"
              value={campaign.selectedQueue}
              onChange={handleChange}
            >
              <option>Select Call Group...</option>
              {callGroupList.map((center) => (
                <option key={center.id} value={center.id}>
                  {center.group_name}
                </option>
              ))}
            </Form.Select>
          )}
        </>
      )}

      {usesCallPatchSingleCallGroup(campaign) && (
        <>
          {isCallGroupForced(campaign) ? (
            <div style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
              <span style={{ color: 'red', marginRight: '4px' }}>*</span>
              Select Call Group
            </div>
          ) : (
            <Form.Check
              type="checkbox"
              id="selectQueuePatchSingle"
              label={
                <>
                  <span style={{ color: 'red', marginRight: '4px' }}>*</span>
                  Select Call Group
                </>
              }
              name="agentAssignment"
              value="queue"
              checked={campaign.agentAssignment === 'queue'}
              onChange={() =>
                setCampaign((prev) => ({
                  ...prev,
                  agentAssignment: prev.agentAssignment === 'queue' ? '' : 'queue',
                  selectedQueue: prev.agentAssignment === 'queue' ? '' : prev.selectedQueue,
                  assignedQueues: prev.agentAssignment === 'queue' ? [] : prev.assignedQueues,
                }))
              }
              style={{ marginBottom: '0.75rem' }}
            />
          )}
          {campaign.agentAssignment === 'queue' && (
            <Form.Select
              style={{ marginTop: '0.5rem', marginBottom: '1rem' }}
              name="selectedQueue"
              value={campaign.assignedQueues[0]?.queueId || ''}
              onChange={(e) => {
                const queueId = e.target.value;
                const queueName = callGroupList.find((q) => String(q.id) === String(queueId))?.group_name;
                setCampaign((prev) => ({
                  ...prev,
                  selectedQueue: queueId,
                  assignedQueues: queueId ? [{ queueId, queueName, dtmf: null }] : [],
                }));
                if (fieldErrors.assignedQueues) {
                  setFieldErrors((prev) => ({ ...prev, assignedQueues: '' }));
                }
              }}
            >
              <option value="">Select Call Group...</option>
              {callGroupList.map((center) => (
                <option key={center.id} value={center.id}>
                  {center.group_name}
                </option>
              ))}
            </Form.Select>
          )}
          <FieldError message={fieldErrors.assignedQueues} />
        </>
      )}

      {usesCallGroupMultiAssign(campaign) && (
        <>
          {isCallGroupForced(campaign) ? (
            <div style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
              <span style={{ color: 'red', marginRight: '4px' }}>*</span>
              Select Call Group
            </div>
          ) : (
            <Form.Check
              type="checkbox"
              id={isClick2CallCampaign(campaign) ? 'selectQueueClick2Call' : 'selectQueuePatch'}
              label={
                <>
                  <span style={{ color: 'red', marginRight: '4px' }}>*</span>
                  Select Call Group
                </>
              }
              name="agentAssignment"
              value="queue"
              checked={campaign.agentAssignment === 'queue'}
              onChange={() =>
                setCampaign((prev) => ({
                  ...prev,
                  agentAssignment: prev.agentAssignment === 'queue' ? '' : 'queue',
                }))
              }
              style={{ marginBottom: '0.75rem' }}
            />
          )}

          {campaign.agentAssignment === 'queue' && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              <Form.Select
                style={{ flex: 2, minWidth: '200px' }}
                name="selectedQueue"
                value={campaign.selectedQueue}
                onChange={(e) => {
                  const value = e.target.value;
                  setCampaign((prev) => ({ ...prev, selectedQueue: value, selectedAgentsForQueue: [] }));
                  if (fieldErrors.selectedQueue) setFieldErrors((prev) => ({ ...prev, selectedQueue: '' }));
                }}
              >
                <option value="">Select Call Group...</option>
                {callGroupList
                  .filter((center) => {
                    if (isClick2CallCampaign(campaign)) {
                      return !campaign.assignedQueues.some((item) => String(item.queueId) === String(center.id));
                    }
                    if (
                      isAutoDialerCampaign(campaign) &&
                      String(campaign.template_id) === '2' &&
                      !campaign.enableCallPatchAdditionalSettings
                    ) {
                      return !campaign.assignedQueues.some((item) => String(item.queueId) === String(center.id));
                    }
                    return true;
                  })
                  .map((center) => (
                    <option key={center.id} value={center.id}>
                      {center.group_name}
                    </option>
                  ))}
              </Form.Select>

              {isClick2CallCampaign(campaign) ? (
                <div style={{ flex: 2, minWidth: '220px' }}>
                  <Select
                    isMulti
                    isDisabled={!campaign.selectedQueue || loadingGroupAgents}
                    placeholder={loadingGroupAgents ? 'Loading agents...' : 'Select agents'}
                    options={groupAgentsList.map((agent) => ({ value: agent.id, label: agent.name }))}
                    value={groupAgentsList
                      .filter((agent) =>
                        (campaign.selectedAgentsForQueue || []).some((id) => String(id) === String(agent.id)),
                      )
                      .map((agent) => ({ value: agent.id, label: agent.name }))}
                    onChange={(selected: any) => {
                      setCampaign((prev) => ({
                        ...prev,
                        selectedAgentsForQueue: (selected || []).map((opt) => opt.value),
                      }));
                      if (selected?.length) setFieldErrors((prev) => ({ ...prev, assignedQueues: '' }));
                    }}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderRadius: '6px',
                        border: fieldErrors.assignedQueues ? '1px solid red' : '1px solid #ced4da',
                        minHeight: '38px',
                      }),
                      menu: (base) => ({ ...base, zIndex: 5 }),
                    }}
                    menuPlacement="auto"
                    className="theme-react-select"
                    classNamePrefix="theme-react-select"
                  />
                </div>
              ) : usesCallGroupDtmf(campaign) ? (
                <Form.Select
                  style={{ flex: 1, minWidth: '120px' }}
                  name="selectedDtmf"
                  value={campaign.selectedDtmf}
                  onChange={handleChange}
                >
                  <option value="">DTMF</option>
                  {[...Array(10).keys()].map((num) => (
                    <option
                      key={num}
                      value={num}
                      disabled={campaign.assignedQueues.some((item) => item.dtmf === String(num))}
                    >
                      {num}
                    </option>
                  ))}
                </Form.Select>
              ) : null}

              <Button
                type="button"
                variant="default"
                aria-label="Add call group"
                className="h-10 w-10 shrink-0 p-0 text-xl font-semibold leading-none"
                onClick={() => {
                  if (isClick2CallCampaign(campaign)) {
                    if (!campaign.selectedQueue || !campaign.selectedAgentsForQueue?.length) return;
                    if (
                      campaign.assignedQueues.some(
                        (item) => String(item.queueId) === String(campaign.selectedQueue),
                      )
                    ) {
                      toast.error('This call group is already added.');
                      return;
                    }
                    const agentLabels = campaign.selectedAgentsForQueue.map((id) => {
                      const agent = groupAgentsList.find((a) => String(a.id) === String(id));
                      return agent?.name || String(id);
                    });
                    setCampaign((prev) => ({
                      ...prev,
                      assignedQueues: [
                        ...prev.assignedQueues,
                        {
                          queueId: prev.selectedQueue,
                          queueName: callGroupList.find((q) => String(q.id) === String(prev.selectedQueue))?.group_name,
                          agentIds: prev.selectedAgentsForQueue,
                          agentLabels,
                        },
                      ],
                      selectedQueue: '',
                      selectedAgentsForQueue: [],
                    }));
                  } else if (
                    campaign.selectedQueue &&
                    (usesCallGroupDtmf(campaign) ? campaign.selectedDtmf : true)
                  ) {
                    if (
                      !usesCallGroupDtmf(campaign) &&
                      campaign.assignedQueues.some(
                        (item) => String(item.queueId) === String(campaign.selectedQueue),
                      )
                    ) {
                      toast.error('This call group is already added.');
                      return;
                    }
                    setCampaign((prev) => ({
                      ...prev,
                      assignedQueues: [
                        ...prev.assignedQueues,
                        {
                          queueId: campaign.selectedQueue,
                          queueName: callGroupList.find((q) => q.id === campaign.selectedQueue)?.group_name,
                          dtmf: usesCallGroupDtmf(campaign) ? campaign.selectedDtmf : null,
                        },
                      ],
                      selectedQueue: '',
                      selectedDtmf: '',
                    }));
                  } else {
                    return;
                  }
                  setFieldErrors((prev) => ({ ...prev, assignedQueues: '' }));
                }}
              >
                +
              </Button>
            </div>
          )}

          {campaign.assignedQueues.length > 0 && (
            <Table bordered hover style={{ marginTop: '1rem' }}>
              <thead>
                <tr>
                  <th>Dialer Group</th>
                  {(isClick2CallCampaign(campaign) || usesCallGroupDtmf(campaign)) && (
                    <th>{isClick2CallCampaign(campaign) ? 'Agents' : 'DTMF'}</th>
                  )}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {campaign.assignedQueues.map((item, index) => (
                  <tr key={index}>
                    <td>{item.queueName}</td>
                    {(isClick2CallCampaign(campaign) || usesCallGroupDtmf(campaign)) && (
                      <td>
                        {isClick2CallCampaign(campaign)
                          ? (item.agentLabels || []).join(', ')
                          : item.dtmf}
                      </td>
                    )}
                    <td>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        aria-label="Remove call group"
                        className="h-8 w-8 p-0 text-lg font-semibold leading-none"
                        onClick={() => {
                          setCampaign((prev) => ({
                            ...prev,
                            assignedQueues: prev.assignedQueues.filter((_, i) => i !== index),
                          }));
                        }}
                      >
                        –
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <FieldError message={fieldErrors.assignedQueues} />
        </>
      )}
    </>
  );

  const renderUploadContacts = () => (
    <div>
      <hr style={{ margin: '1.5rem 0', borderColor: '#eaeaea' }} />
      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
          <Req />
          Contact upload method
        </Form.Label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <RadioCard
            name="contactUploadMethod"
            value="manual"
            checked={campaign.contactUploadMethod === 'manual'}
            onChange={handleChange}
            label="Manual input"
            description="Paste 10-digit numbers, one per line."
          />
          <RadioCard
            name="contactUploadMethod"
            value="upload_file"
            checked={campaign.contactUploadMethod === 'upload_file'}
            onChange={handleChange}
            label="Upload file"
            description="Upload a CSV / TXT list of contacts."
          />
          <RadioCard
            name="contactUploadMethod"
            value="existing"
            checked={campaign.contactUploadMethod === 'existing'}
            onChange={handleChange}
            label="Existing contact"
            description="Reuse a previously uploaded contact list."
          />
          <RadioCard
            name="contactUploadMethod"
            value="contact_group"
            checked={campaign.contactUploadMethod === 'contact_group'}
            onChange={handleChange}
            label="Contact group"
            description="Target a saved contact group."
          />
        </div>
        <FieldError message={fieldErrors.contactUploadMethod} />
      </Form.Group>

      {!!campaign.contactUploadMethod && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Check
            type="checkbox"
            id="removeDuplicates"
            label="Remove duplicates"
            name="removeDuplicates"
            checked={!!campaign.removeDuplicates}
            onChange={handleChange}
          />
          <small style={{ color: '#6c757d', display: 'block', marginTop: '0.35rem' }}>
            When enabled, numbers repeated inside each base are removed. Bases are not compared with each other.
          </small>
        </Form.Group>
      )}

      {campaign.contactUploadMethod === 'contact_group' && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
            <Req />
            Select contact group
          </Form.Label>
          <Form.Select
            name="call_group"
            value={campaign.call_group?.[0] || ''}
            onChange={(e) => {
              const value = e.target.value;
              setCampaign((prev) => ({ ...prev, call_group: value ? [value] : [] }));
              if (value) setFieldErrors((prev) => ({ ...prev, call_group: '' }));
            }}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: fieldErrors.call_group ? '1px solid red' : '1px solid #ced4da',
            }}
          >
            <option value="">Choose a contact group</option>
            {contactGroupList.map((group) => (
              <option key={group.id} value={group.id} disabled={!Number(group.member_count)}>
                {contactGroupLabel(group)}
              </option>
            ))}
          </Form.Select>
          <FieldError message={fieldErrors.call_group} />
        </Form.Group>
      )}

      {campaign.contactUploadMethod === 'manual' && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
            <Req />
            Contact numbers (max {MAX_MANUAL_CONTACTS.toLocaleString()})
          </Form.Label>
          <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            Enter one 10-digit number per line. File will be saved as manual_[timestamp].csv automatically.
          </p>
          <Form.Control
            as="textarea"
            rows={8}
            name="manualContactNumbers"
            value={campaign.manualContactNumbers}
            onChange={handleChange}
            placeholder={'9876543210\n9123456789'}
            style={{
              borderRadius: '6px',
              border: fieldErrors.manualContactNumbers ? '1px solid red' : '1px solid #ced4da',
            }}
          />
          <FieldError message={fieldErrors.manualContactNumbers} />
        </Form.Group>
      )}

      {campaign.contactUploadMethod === 'upload_file' && (
        <>
          <Form.Group style={{ marginBottom: '1.5rem' }}>
            <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
              Download the sample template: a &quot;Number&quot; header, then one 10-digit number
              (starting with 6–9) per line, digits only. Extra columns after the number are dropped;
              the saved base keeps only the numbers.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={downloadNumberTemplate}
            >
              Download Template
            </Button>
          </Form.Group>
          <Form.Group style={{ marginBottom: '1.5rem' }}>
            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              Choose file
            </Form.Label>
            <Form.Control
              type="file"
              accept=".csv,.txt"
              onChange={(e: any) => {
                const file = e.target.files?.[0] || null;
                setContactUploadFile(file);
                setCampaign((prev) => ({ ...prev, base_id: null }));
                if (file) setFieldErrors((prev) => ({ ...prev, contactUploadFile: '' }));
              }}
              style={{
                borderRadius: '6px',
                border: fieldErrors.contactUploadFile ? '1px solid red' : '1px solid #ced4da',
              }}
            />
            <FieldError message={fieldErrors.contactUploadFile} />
          </Form.Group>
        </>
      )}

      {campaign.contactUploadMethod === 'existing' && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
            <Req />
            Select existing contact file
          </Form.Label>
          <Form.Select
            name="existingVoiceBaseId"
            value={campaign.existingVoiceBaseId || ''}
            onChange={handleChange}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: fieldErrors.existingVoiceBaseId ? '1px solid red' : '1px solid #ced4da',
            }}
          >
            <option value="">Choose an existing contact file</option>
            {voiceBaseList.map((base) => (
              <option key={base.base_id} value={base.base_id}>
                {base.base_name} ({base.total_numbers} contacts)
              </option>
            ))}
          </Form.Select>
          <FieldError message={fieldErrors.existingVoiceBaseId} />
          {existingBasePreview && (
            <div style={{ marginTop: '0.75rem' }}>
              {existingBasePreview.loading ? (
                <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>Loading numbers…</div>
              ) : existingBasePreview.error ? (
                <div style={{ fontSize: '0.875rem', color: '#dc3545' }}>{existingBasePreview.error}</div>
              ) : (
                <>
                  <div style={{ fontSize: '0.875rem', color: '#495057', marginBottom: '0.375rem' }}>
                    Numbers in this file: {existingBasePreview.total.toLocaleString()}
                    {existingBasePreview.truncated
                      ? ` (showing first ${existingBasePreview.count.toLocaleString()})`
                      : ''}
                  </div>
                  {existingBasePreview.warning && (
                    <div style={{ fontSize: '0.8125rem', color: '#92400e', marginBottom: '0.375rem' }}>
                      {existingBasePreview.warning}
                    </div>
                  )}
                  {/* ponytail: read-only textarea, cheap for 50K+ lines (no per-row DOM). */}
                  <Form.Control
                    as="textarea"
                    readOnly
                    value={existingBasePreview.text}
                    rows={8}
                    placeholder="No numbers found"
                    style={{
                      maxHeight: '220px',
                      resize: 'none',
                      overflowY: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.8125rem',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #ced4da',
                    }}
                  />
                </>
              )}
            </div>
          )}
        </Form.Group>
      )}

      {!shouldHideDispositionForm(campaign) && (
        <Form.Group style={{ marginBottom: '1.5rem' }}>
          <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
            Disposition Form
          </Form.Label>
          <Form.Select
            name="selectedDispositionForm"
            value={campaign.selectedDispositionForm || ''}
            onChange={(e) => {
              const value = e.target.value;
              setCampaign((prev) => ({ ...prev, selectedDispositionForm: value ? value : null }));
            }}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: '1px solid #ced4da',
            }}
          >
            <option value="">None (use default form)</option>
            {Array.isArray(filteredDispositionForms) &&
              filteredDispositionForms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.name} ({formatDispositionCallDirectionLabel(form.call_direction)})
                </option>
              ))}
          </Form.Select>
        </Form.Group>
      )}
    </div>
  );

  const renderScript = () => (
    <div>
      <hr style={{ margin: '1.5rem 0', borderColor: '#eaeaea' }} />
      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <div className="mb-3 flex flex-wrap gap-2">
          <RadioCard
            name="scriptOption"
            value="none"
            checked={campaign.scriptOption === 'none'}
            onChange={handleChange}
            label="Doesn't require script"
            description="Skip the script step; agents dial without an on-screen prompt."
          />
          <RadioCard
            name="scriptOption"
            value="existing"
            checked={campaign.scriptOption === 'existing'}
            onChange={handleChange}
            label="Existing script"
            description="Pick one that's already been saved for reuse."
          />
          <RadioCard
            name="scriptOption"
            value="new"
            checked={campaign.scriptOption === 'new'}
            onChange={handleChange}
            label="New script"
            description="Compose a fresh script inline below."
          />
        </div>

        {campaign.scriptOption === 'existing' && (
          <>
            <Form.Select
              name="selectedScript"
              value={campaign.selectedScript}
              onChange={handleChange}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: fieldErrors.selectedScript ? '1px solid red' : '1px solid #ced4da',
                marginBottom: '20px',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
              }}
            >
              <option value="">Select Existing Script</option>
              {scripts.map((sc) => (
                <option key={sc.id} value={sc.id}>
                  {sc.title}
                </option>
              ))}
            </Form.Select>
            <FieldError message={fieldErrors.selectedScript} />

            {campaign.selectedScript &&
              (() => {
                const selected = scripts.find((sc) => sc.id === campaign.selectedScript);
                return (
                  selected && (
                    <div
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        padding: '10px',
                        marginBottom: '20px',
                      }}
                    >
                      <h6 style={{ marginBottom: '0.5rem' }}>
                        Short-Description- {stripHtmlTags(selected.short_description)}
                      </h6>
                      <p style={{ marginBottom: 0 }}>
                        Description- {stripHtmlTags(selected.description)}
                      </p>
                    </div>
                  )
                );
              })()}
          </>
        )}

      </Form.Group>

      {campaign.scriptOption === 'new' && (
        <>
          <Form.Group style={{ marginBottom: '1.5rem' }}>
            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              Title
            </Form.Label>
            <Form.Control
              type="text"
              name="scriptTitle"
              value={campaign.scriptTitle}
              onChange={handleChange}
              placeholder="Enter script title"
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: fieldErrors.scriptTitle ? '1px solid red' : '1px solid #ced4da',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
              }}
            />
            <FieldError message={fieldErrors.scriptTitle} />
          </Form.Group>

          <Form.Group style={{ marginBottom: '1.5rem' }}>
            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              Description
            </Form.Label>
            <div
              style={{
                border: fieldErrors.scriptDescription ? '1px solid red' : '1px solid #ced4da',
                borderRadius: '6px',
                overflow: 'hidden',
              }}
            >
              <CKEditor
                editor={ClassicEditor}
                config={SCRIPT_EDITOR_CONFIG}
                data={campaign.scriptDescription}
                config={{
                  toolbar: {
                    items: [
                      'heading', '|',
                      'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                      'outdent', 'indent', '|',
                      'blockQuote', 'insertTable', 'undo', 'redo',
                    ],
                    shouldNotGroupWhenFull: true,
                  },
                }}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setCampaign((prev) => ({ ...prev, scriptDescription: data }));
                  if (data.trim()) setFieldErrors((prev) => ({ ...prev, scriptDescription: '' }));
                }}
                onReady={(editor) => {
                  editor.editing.view.change((writer) => {
                    writer.setStyle('height', '300px', editor.editing.view.document.getRoot());
                  });
                }}
              />
            </div>
            <FieldError message={fieldErrors.scriptDescription} />
          </Form.Group>

          <Form.Group style={{ marginBottom: '1.5rem' }}>
            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              Short Description
            </Form.Label>
            <Form.Control
              type="text"
              maxLength={SCRIPT_SHORT_DESCRIPTION_MAX}
              name="scriptShortDescription"
              value={campaign.scriptShortDescription}
              onChange={handleChange}
              placeholder="Summary of the script"
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: fieldErrors.scriptShortDescription ? '1px solid red' : '1px solid #ced4da',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
              }}
            />
            <FieldError message={fieldErrors.scriptShortDescription} />
          </Form.Group>
        </>
      )}
    </div>
  );

  const renderSettings = () => (
    <div>
      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
          <Req />
          Select DID
        </Form.Label>
        <Select
          isMulti
          name="did"
          options={outboundDidList.map((num) => ({ value: num.id, label: num?.trunkId?.name ? `${num.actual_number} - ${num.trunkId.name}` : num.actual_number }))}
          value={outboundDidList
            .filter((num) => campaign.did?.includes(num.id))
            .map((num) => ({ value: num.id, label: num?.trunkId?.name ? `${num.actual_number} - ${num.trunkId.name}` : num.actual_number }))}
          onChange={(selected: any) => {
            setCampaign((prev) => ({ ...prev, did: selected.map((opt) => opt.value) }));
            if (selected.length > 0) setFieldErrors((prev) => ({ ...prev, did: '' }));
          }}
          styles={{
            control: (provided) => ({
              ...provided,
              borderRadius: '6px',
              border: fieldErrors.did ? '1px solid red' : '1px solid #ced4da',
              padding: '2px',
            }),
            menu: (base) => ({ ...base, zIndex: 5 }),
          }}
          menuPlacement="auto"
          className="theme-react-select"
          classNamePrefix="theme-react-select"
        />
        <FieldError message={fieldErrors.did} />
      </Form.Group>

      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
          <Req />
          Time between calls (seconds)
        </Form.Label>
        <Form.Control
          type="number"
          name="timeBetweenCalls"
          value={campaign.timeBetweenCalls}
          min={0}
          max={90}
          onChange={(e) => {
            let value: any = e.target.value;
            if (value === '') {
              handleChange(e);
              return;
            }
            value = Number(value);
            if (value < 0) value = 0;
            if (value > 90) value = 90;
            handleChange({ target: { name: 'timeBetweenCalls', value } } as any);
          }}
          placeholder="Time interval before calling a new contact (Max 90)"
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '6px',
            border: fieldErrors.timeBetweenCalls ? '1px solid red' : '1px solid #ced4da',
            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
          }}
        />
        <FieldError message={fieldErrors.timeBetweenCalls} />
      </Form.Group>

      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Check
          type="checkbox"
          id="enableMaxRetries"
          label="Enable Retry"
          name="enableMaxRetries"
          checked={!!campaign.enableMaxRetries}
          onChange={handleChange}
          style={{ marginBottom: '0.75rem' }}
        />
        {campaign.enableMaxRetries && (
          <>
            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              When to retry
            </Form.Label>
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <RadioCard
                name="retry_mode"
                value={RETRY_MODE_ON_FAILURE}
                checked={campaign.retry_mode !== RETRY_MODE_AFTER_COMPLETION}
                onChange={handleChange}
                label="Retry on failure"
                description="Retry a failed call during the campaign, after the retry interval (min 15 minutes)."
              />
              <RadioCard
                name="retry_mode"
                value={RETRY_MODE_AFTER_COMPLETION}
                checked={campaign.retry_mode === RETRY_MODE_AFTER_COMPLETION}
                onChange={handleChange}
                label="Retry after all numbers are called"
                description="First every number is called once. Then the failed numbers are called again."
              />
            </div>

            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              Max retries
            </Form.Label>
            <Form.Control
              type="number"
              name="max_retries"
              value={campaign.max_retries}
              onChange={handleChange}
              placeholder="Maximum retries per contact (Limit is 5)"
              min="1"
              max={5}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: fieldErrors.max_retries ? '1px solid red' : '1px solid #ced4da',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
              }}
            />
            <FieldError message={fieldErrors.max_retries} />

            <Form.Label style={{ fontWeight: 500, color: '#495057', marginTop: '1rem', marginBottom: '0.5rem' }}>
              <Req />
              Retry time interval (minutes)
            </Form.Label>
            <Form.Control
              type="number"
              name="retryInterval"
              value={campaign.retryInterval}
              onChange={handleChange}
              placeholder={`Minutes to wait before launching a retry (${minRetryInterval(campaign.retry_mode)}–${RETRY_INTERVAL_MAX})`}
              min={minRetryInterval(campaign.retry_mode)}
              max={RETRY_INTERVAL_MAX}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: fieldErrors.retryInterval ? '1px solid red' : '1px solid #ced4da',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
              }}
            />
            <FieldError message={fieldErrors.retryInterval} />
            <small style={{ color: '#6c757d', display: 'block', marginTop: '0.35rem' }}>
              Retries will be launched only after this interval.
            </small>
          </>
        )}
        <div
          role="note"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.65rem',
            marginTop: '0.85rem',
            padding: '0.75rem 0.9rem',
            borderRadius: '8px',
            background: '#fff8e6',
            border: '1px solid #f0d78c',
            color: '#7a5b00',
          }}
        >
          <FaInfoCircle style={{ flexShrink: 0, marginTop: '2px', color: '#c9a227' }} />
          <span style={{ fontSize: '0.875rem', lineHeight: 1.45 }}>
            <strong>Note:</strong>{' '}
            {campaign.enableMaxRetries && campaign.retry_mode === RETRY_MODE_AFTER_COMPLETION
              ? 'Failed numbers are called again only after every number in the campaign has been called once. Retry does not wait for the campaign end time.'
              : 'If campaign is completed, then no retry will be performed.'}
          </span>
        </div>
      </Form.Group>
    </div>
  );

  const renderSchedule = () => (
    <div>
      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
          Outbound Campaign duration
        </Form.Label>
        <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          Outbound Campaign will not run outside of these dates
        </p>
        <Row>
          <Col md={6}>
            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              Start date
            </Form.Label>
            <CalendarDateInput
              value={campaign.start_date || ''}
              onChange={(v) => handleChange({ target: { value: v, name: 'start_date' } } as any)}
              min={getToday()}
              disabled={pausedEdit}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: fieldErrors.start_date ? '1px solid red' : '1px solid #ced4da',
                width: '100%',
              }}
            />
            <FieldError message={fieldErrors.start_date} />
          </Col>
          <Col md={6}>
            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              End date
            </Form.Label>
            <CalendarDateInput
              value={campaign.end_date || ''}
              onChange={(v) => handleChange({ target: { value: v, name: 'end_date' } } as any)}
              min={isEdit && (campaign.start_date || '') < getToday() ? getToday() : campaign.start_date || ''}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: fieldErrors.end_date ? '1px solid red' : '1px solid #ced4da',
                width: '100%',
              }}
            />
            <FieldError message={fieldErrors.end_date} />
          </Col>
        </Row>
      </Form.Group>

      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
          <Req />
          Week days
        </Form.Label>
        <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Select days to run campaign</p>
        <Button
          size="sm"
          onClick={handleSelectAllToggle}
          style={{ marginBottom: '1rem' }}
          disabled={
            (campaign.start_date && campaign.end_date && campaign.start_date === campaign.end_date) ||
            (!campaign.start_date || !campaign.end_date)
          }
        >
          {selectAll ? 'Deselect All' : 'Select All'}
        </Button>
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '2rem' }}>
          {allDays.map((day) => {
            const isRestricted = dateDiff !== null && dateDiff <= 6;
            const isDisabled = isRestricted && !allowedDays.includes(day);
            return (
              <div key={day} style={{ padding: '0.5rem 0', borderBottom: '1px solid #f0f0f0' }}>
                <Form.Check
                  type="checkbox"
                  id={day}
                  label={day}
                  value={day}
                  checked={campaign?.weekDays?.includes(day)}
                  onChange={(e) => handleDayChange(day, e.target.checked)}
                  disabled={isDisabled || !campaign.start_date || !campaign.end_date}
                />
              </div>
            );
          })}
        </div>
        <FieldError message={fieldErrors.weekDays} />
      </Form.Group>

      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
          <Req />
          Outbound Campaign hours
        </Form.Label>
        <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          {isPromotionalAccount(user)
            ? 'Promotional accounts can schedule any time between 09:00 and 21:00.'
            : 'Outbound Campaign will not run outside of these hours'}
        </p>
        <div style={{ marginBottom: '0.75rem' }}>
          <Form.Check
            type="checkbox"
            className="form-switch"
            id="strict_timings"
            role="switch"
            label={`Strict timings: ${isStrictTimings(campaign.strict_timings) ? 'On' : 'Off'}`}
            checked={isStrictTimings(campaign.strict_timings)}
            onChange={(e) => handleStrictTimingsToggle(e.target.checked)}
          />
          <p style={{ color: '#6c757d', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            {isStrictTimings(campaign.strict_timings)
              ? 'On: the From / To hours below apply on every day of the campaign.'
              : isPromotionalAccount(user)
                ? `Off (9 to 21): the start date runs from the From time; every later day runs ${AUTO_DIALER_SCHEDULE_START}–${AUTO_DIALER_SCHEDULE_END}.`
                : 'Off (all day): the start date runs from the From time; every later day runs 00:00–23:59.'}
          </p>
        </div>
        <Row>
          <Col md={6}>
            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              From
            </Form.Label>
            <CalendarTimeInput
              value={campaign.startHour || ''}
              onChange={(v) => handleChange({ target: { value: v, name: 'startHour' } } as any)}
              min={
                isPromotionalAccount(user)
                  ? startMinApplies(campaign)
                    ? clampToPromotionalWindow(getMinStartTime(minStartTimeMode(campaign.strict_timings)))
                    : AUTO_DIALER_SCHEDULE_START
                  : startMinApplies(campaign)
                    ? getMinStartTime(minStartTimeMode(campaign.strict_timings))
                    : undefined
              }
              max={isPromotionalAccount(user) ? AUTO_DIALER_SCHEDULE_END : undefined}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: fieldErrors.startHour ? '1px solid red' : '1px solid #ced4da',
                width: '100%',
              }}
              disabled={!campaign.start_date || !campaign.end_date}
            />
            <FieldError message={fieldErrors.startHour} />
          </Col>
          <Col md={6}>
            <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
              <Req />
              To
            </Form.Label>
            <CalendarTimeInput
              value={campaign.endHour || ''}
              onChange={(v) => handleChange({ target: { value: v, name: 'endHour' } } as any)}
              min={(() => {
                // Promotional floor is 09:00; a campaign ending today also can't end before now.
                const floor = isPromotionalAccount(user) ? AUTO_DIALER_SCHEDULE_START : undefined;
                if (campaign.end_date !== getToday()) return floor;
                const now = currentHHMM();
                return floor && floor > now ? floor : now;
              })()}
              max={isPromotionalAccount(user) ? AUTO_DIALER_SCHEDULE_END : undefined}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: fieldErrors.endHour ? '1px solid red' : '1px solid #ced4da',
                width: '100%',
              }}
              disabled={!campaign.start_date || !campaign.end_date}
            />
            <FieldError message={fieldErrors.endHour} />
          </Col>
        </Row>
      </Form.Group>

      {/* Daily pause slots — saved with the schedule; the scheduler pauses dialing in them. */}
      <Form.Group style={{ marginBottom: '1.5rem' }}>
        <Form.Label style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}>
          Pause timings
        </Form.Label>
        <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          {pausesAllowed()
            ? 'Optional. Add up to 3 daily breaks, each at least 10 minutes long. The campaign will pause dialing during these breaks.'
            : 'Available when the campaign runs for more than 1 hour a day.'}
        </p>
        {pausesAllowed() ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowPauseModal(true)}
            disabled={!campaign.start_date || !campaign.end_date}
          >
            {(campaign.pause_slots || []).length ? 'Edit pause timings' : 'Add pause timings'}
          </Button>
          {(campaign.pause_slots || []).map((s, i) => (
            <span
              key={`${s.start_time}-${i}`}
              className="inline-flex items-center rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-medium tabular-nums text-amber-700 dark:text-amber-300"
            >
              <span className="mr-1 font-semibold">{pauseLabel(s, i)}</span>
              {s.start_time}–{s.end_time}
            </span>
          ))}
        </div>
        ) : null}
        <FieldError message={fieldErrors.pause_slots} />
        <PauseSlotsModal
          open={showPauseModal}
          onOpenChange={setShowPauseModal}
          slots={campaign.pause_slots || []}
          window={currentPauseWindow()}
          onSave={(slots) => {
            setCampaign((prev) => ({ ...prev, pause_slots: slots }));
            setFieldErrors((prev) => ({ ...prev, pause_slots: '' }));
          }}
        />
      </Form.Group>
    </div>
  );

  const renderSms = () => (
    <div>
      <>
        <Form.Group style={{ marginBottom: '1.5rem', display: 'flex', gap: 10, marginTop: '1.5rem', flexWrap: 'wrap' }}>
          <Form.Check
            type="checkbox"
            label="Send SMS on Success"
            name="sendSmsOnSuccess"
            checked={campaign.sendSmsOnSuccess}
            onChange={handleChange}
          />
          {campaign.sendSmsOnSuccess && (
            <>
              <div>
                <label>Select Duration</label>
                <Form.Select name="duration" value={campaign.duration} onChange={handleChange} style={{ width: 250 }}>
                  <option value="">Select duration</option>
                  {Array.from({ length: 60 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div>
                <label>Select SMS Webhook</label>
                <Form.Select
                  name="successSmsWebhook"
                  value={campaign.successSmsWebhook}
                  onChange={handleChange}
                  style={{
                    width: 250,
                    border: fieldErrors.successSmsWebhook ? '1px solid red' : '1px solid #ced4da',
                  }}
                >
                  <option value="">Select SMS Webhook</option>
                  {smsWebhooks?.map((webhook) => (
                    <option key={webhook.id} value={webhook.id}>
                      {webhook.webhook_name}
                    </option>
                  ))}
                </Form.Select>
                <FieldError message={fieldErrors.successSmsWebhook} />
              </div>
            </>
          )}
        </Form.Group>

        <Form.Group style={{ marginBottom: '1.5rem', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Form.Check
            type="checkbox"
            label="Send SMS on Fail"
            name="sendSmsOnFail"
            checked={campaign.sendSmsOnFail}
            onChange={handleChange}
          />
          {campaign.sendSmsOnFail && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Form.Select
                name="failSmsWebhook"
                value={campaign.failSmsWebhook}
                onChange={handleChange}
                style={{
                  width: 250,
                  border: fieldErrors.failSmsWebhook ? '1px solid red' : '1px solid #ced4da',
                }}
              >
                <option value="">Select SMS Webhook</option>
                {smsWebhooks?.map((webhook) => (
                  <option key={webhook.id} value={webhook.id}>
                    {webhook.webhook_name}
                  </option>
                ))}
              </Form.Select>
              <FieldError message={fieldErrors.failSmsWebhook} />
            </div>
          )}
        </Form.Group>

        <Form.Group style={{ marginBottom: '1.5rem', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Form.Check
            type="checkbox"
            label="Enable Webhook"
            name="enableWebhook"
            checked={campaign.enableWebhook}
            onChange={handleChange}
          />
          {campaign.enableWebhook && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Form.Select
                name="webhook"
                value={campaign.webhook}
                onChange={handleChange}
                style={{
                  width: 250,
                  border: fieldErrors.webhook ? '1px solid red' : '1px solid #ced4da',
                }}
              >
                <option value="">Select Webhook</option>
                {webhooks?.map((webhook) => (
                  <option key={webhook.id} value={webhook.id}>
                    {webhook.webhook_name}
                  </option>
                ))}
              </Form.Select>
              <FieldError message={fieldErrors.webhook} />
            </div>
          )}
        </Form.Group>
      </>

      {(campaign.template_id === '1' || campaign.template_id === '2') && campaign.campaign_type == 2 && (
        <Form.Group style={{ marginBottom: '1.5rem', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Form.Check
            type="checkbox"
            label="Send Sms on DTMF"
            name="enableSmsOnDtmf"
            checked={campaign.enableSmsOnDtmf}
            onChange={handleChange}
          />
          {campaign.enableSmsOnDtmf && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Form.Select
                name="smsOnDtmf"
                value={campaign.smsOnDtmf}
                onChange={handleChange}
                style={{ border: fieldErrors.smsOnDtmf ? '1px solid red' : '1px solid #ced4da' }}
              >
                <option value="">Select Sms Webhook</option>
                {smsWebhooks?.map((webhook) => (
                  <option key={webhook.id} value={webhook.id}>
                    {webhook.webhook_name}
                  </option>
                ))}
              </Form.Select>
              <FieldError message={fieldErrors.smsOnDtmf} />
            </div>
          )}
        </Form.Group>
      )}

      {campaign.template_id === '2' && campaign.campaign_type == 2 && (
        <>
          <Form.Group style={{ marginBottom: '1.5rem', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Form.Check
              type="checkbox"
              label="Send Sms to Agent on Patch Success"
              name="enableSmsOnPatchSuccess"
              checked={campaign.enableSmsOnPatchSuccess}
              onChange={handleChange}
            />
            {campaign.enableSmsOnPatchSuccess && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Form.Select
                  name="smsOnPatchSuccess"
                  value={campaign.smsOnPatchSuccess}
                  onChange={handleChange}
                  style={{
                    width: 250,
                    border: fieldErrors.smsOnPatchSuccess ? '1px solid red' : '1px solid #ced4da',
                  }}
                >
                  <option value="">Select Sms Webhook</option>
                  {smsWebhooks?.map((webhook) => (
                    <option key={webhook.id} value={webhook.id}>
                      {webhook.webhook_name}
                    </option>
                  ))}
                </Form.Select>
                <FieldError message={fieldErrors.smsOnPatchSuccess} />
              </div>
            )}
          </Form.Group>

          <Form.Group style={{ marginBottom: '1.5rem', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Form.Check
              type="checkbox"
              label="Send Sms to Agent on Patch Failed"
              name="enableSmsOnPatchFailed"
              checked={campaign.enableSmsOnPatchFailed}
              onChange={handleChange}
            />
            {campaign.enableSmsOnPatchFailed && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Form.Select
                  name="smsOnPatchFailed"
                  value={campaign.smsOnPatchFailed}
                  onChange={handleChange}
                  style={{
                    width: 250,
                    border: fieldErrors.smsOnPatchFailed ? '1px solid red' : '1px solid #ced4da',
                  }}
                >
                  <option value="">Select Sms Webhook</option>
                  {smsWebhooks?.map((webhook) => (
                    <option key={webhook.id} value={webhook.id}>
                      {webhook.webhook_name}
                    </option>
                  ))}
                </Form.Select>
                <FieldError message={fieldErrors.smsOnPatchFailed} />
              </div>
            )}
          </Form.Group>
        </>
      )}
    </div>
  );

  const currentStepErrors = computeStepErrors(currentStepKey);
  const currentStepErrorMessages = Object.values(currentStepErrors).filter(Boolean);
  const isCurrentStepValid = currentStepErrorMessages.length === 0;

  const continueButtonStyle: any = {
    padding: '0.5rem 1.75rem',
    fontWeight: 500,
    ...(isCurrentStepValid
      ? {}
      : {
          background: 'hsl(var(--muted))',
          borderColor: 'hsl(var(--border))',
          color: 'hsl(var(--muted-foreground))',
          cursor: 'not-allowed',
          boxShadow: 'none',
          opacity: 1,
          pointerEvents: 'none',
        }),
  };

  const createButtonStyle: any = {
    padding: '0.5rem 1.75rem',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    ...(buttonDisable
      ? { opacity: 0.85, cursor: 'wait' }
      : isCurrentStepValid
        ? {}
        : {
            background: 'hsl(var(--muted))',
            borderColor: 'hsl(var(--border))',
            color: 'hsl(var(--muted-foreground))',
            cursor: 'not-allowed',
            boxShadow: 'none',
            opacity: 1,
            pointerEvents: 'none',
          }),
  };

  const stepTitleMap: any = {
    basics: 'Basics',
    type: 'Type',
    setup: 'Setup',
    dialer: 'Dialer Type',
    template: 'Template',
    general: 'General',
    upload_contacts: 'Upload Contacts',
    bases: 'Uploaded Bases',
    script: 'Script',
    settings: 'Outbound Campaign Settings',
    schedule: 'Scheduling',
    sms: 'Sms/Webhook Settings',
  };

  const renderStepIcon = (iconKey) => {
    const iconStyle = { fontSize: '1.1rem' };
    switch (iconKey) {
      case 'microchip': return <FaMicrochip style={iconStyle} />;
      case 'template': return <FaNewspaper style={iconStyle} />;
      case 'globe': return <FaGlobe style={iconStyle} />;
      case 'upload': return <FaUpload style={iconStyle} />;
      case 'script': return <FaFileAlt style={iconStyle} />;
      case 'settings': return <FaCog style={iconStyle} />;
      case 'schedule': return <FaCalendarAlt style={iconStyle} />;
      case 'sms': return <FaCommentDots style={iconStyle} />;
      default: return null;
    }
  };

  const getTabStyle = (tabIndex): any => {
    const isLocked = tabIndex > unlockedUpToIndex;
    const isActive = stepIndex === tabIndex;
    const isCompleted = completedSteps.includes(tabIndex) && !isActive;
    const isInvalid = invalidSteps.includes(tabIndex) && !isActive;

    // One unified palette: soft muted at rest, primary tint when active,
    // subtle emerald wash when previously completed, subtle rose wash
    // when invalid. Backgrounds are the primary read; the bottom border
    // just anchors the active step. No more clashing bright green/red/
    // purple accents on adjacent tabs.
    let color = 'hsl(var(--muted-foreground))';
    let background = 'transparent';
    let borderBottom = '2px solid transparent';

    if (isActive) {
      color = 'hsl(var(--primary))';
      background = 'hsl(var(--primary) / 0.10)';
      borderBottom = '2px solid hsl(var(--primary))';
    } else if (isCompleted) {
      color = 'rgb(4, 120, 87)'; // emerald-700
      background = 'rgba(16, 185, 129, 0.08)';
      borderBottom = '2px solid rgba(16, 185, 129, 0.35)';
    } else if (isInvalid) {
      color = 'rgb(190, 18, 60)'; // rose-700
      background = 'rgba(244, 63, 94, 0.08)';
      borderBottom = '2px solid rgba(244, 63, 94, 0.35)';
    }

    if (isLocked) color = 'hsl(var(--muted-foreground) / 0.6)';

    return {
      color,
      background,
      borderBottom,
      fontWeight: isActive ? 600 : 500,
      padding: '0.75rem 1rem',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
      border: '1px solid transparent',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem',
      position: 'relative',
      zIndex: 2,
      boxShadow: 'none',
      cursor: isLocked ? 'not-allowed' : 'pointer',
      opacity: isLocked ? 0.55 : 1,
    };
  };

  // Edit banner: contacts are locked, so just name what the campaign dials.
  const lockedContactChips = isEdit
    ? [
        ...(campaign.call_group || []).map(
          (id) =>
            contactGroupList.find((g) => String(g.id) === String(id))?.group_name || `Contact group #${id}`,
        ),
        ...(campaign.base_id ? [`Uploaded contact file #${campaign.base_id}`] : []),
      ]
    : [];

  if (!canCreateCampaign()) {
    return <UploadNotPermitted title={isEdit ? 'Edit Campaign' : 'Create Campaign'} what="campaign contact files" />;
  }

  return (
    <FillPage>
      <PageHeader
        title={isEdit ? 'Edit Campaign' : 'Create Campaign'}
        description={
          isEdit
            ? 'Ready or paused campaign. Dialer type, template and contacts cannot be changed.'
            : 'Compose a voice campaign step by step — dialer, template, contacts, schedule.'
        }
        actions={
          <Button variant="outline" onClick={() => navigate('/campaigns')}>
            <FaArrowLeft style={{ fontSize: '0.85rem' }} />
            Back
          </Button>
        }
      />
      {isEdit && (
        <div
          className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm leading-relaxed text-amber-900 dark:text-amber-200"
          role="note"
        >
          {/* <div className="font-semibold">Editing a ready or paused campaign</div> */}
          {/* <div>
            Greyed-out fields are locked: Dialer Type, First Call Connect and Template. Existing contacts cannot be changed; new bases can be added below.
            Start Date can be moved to today or a later date. Changes apply when the campaign starts or resumes.
          </div> */}
          <div className="mt-1">
            Note: once saved, changes may take up to 5 minutes to fully sync across active call groups.
          </div>
          {/* <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium">Contacts (read-only):</span>
            {lockedContactChips.length > 0 ? (
              lockedContactChips.map((label) => (
                <span
                  key={label}
                  className="rounded-full bg-background px-2 py-0.5 text-xs font-medium text-foreground ring-1 ring-inset ring-border"
                >
                  {label}
                </span>
              ))
            ) : (
              <span className="text-xs">—</span>
            )}
          </div> */}
        </div>
      )}
      {/* {alertMessage && (
        <div
          className="mb-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm leading-relaxed text-emerald-700 dark:text-emerald-400"
          role="status"
        >
          {alertMessage}
        </div>
      )} */}
      <Container
        className="campaigns-create-page"
        style={{
          padding: 0,
          borderRadius: '10px',
          overflow: 'visible',
        }}
      >
        <Card style={{ border: 'none', overflow: 'visible' }}>
          <Card.Header
            style={{
              padding: '0',
              borderRadius: '8px 8px 0 0',
              background: 'hsl(var(--card))',
              borderBottom: '1px solid hsl(var(--border) / 0.6)',
            }}
          >
            <Nav variant="tabs" defaultActiveKey="#first" style={{ borderBottom: 'none', padding: '0.5rem 0.5rem 0', gap: '0.25rem' }}>
              {stepFlow.map((stepDef, idx) => (
                <Nav.Item key={stepDef.key}>
                  <Nav.Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleTabClick(idx);
                    }}
                    active={stepIndex === idx}
                    style={getTabStyle(idx)}
                  >
                    {renderStepIcon(stepDef.icon)}
                    {stepDef.label}
                    {completedSteps.includes(idx) && stepIndex !== idx && (
                      <FaCheckCircle style={{ color: 'rgb(4, 120, 87)', marginLeft: '4px', fontSize: '0.85rem', opacity: 0.85 }} />
                    )}
                    {invalidSteps.includes(idx) && stepIndex !== idx && (
                      <FaWindowClose style={{ color: 'rgb(190, 18, 60)', marginLeft: '4px', fontSize: '0.85rem', opacity: 0.85 }} />
                    )}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Card.Header>

          <Card.Body style={{ padding: '2rem', overflow: 'visible' }}>
            <Card.Title style={{ color: '#212529', fontWeight: 500 }}>
              {stepTitleMap[currentStepKey] || ''}
            </Card.Title>
            {/* No step-level error banner — each field renders its own
                inline `<FieldError>` in red under the input, populated
                by `nextStep`'s `setFieldErrors(errors)` on failed
                advance. Matches the pattern used by Trunks / Companies. */}
            {renderStep()}
          </Card.Body>

          <Card.Footer style={{ borderTop: '1px solid #eaeaea', padding: '1rem' }}>
            <Row>
              <Col>
                {stepIndex > 0 && (
                  <Button variant="secondary" onClick={prevStep} style={{ padding: '0.5rem 1.75rem' }}>
                    Back
                  </Button>
                )}
              </Col>
              <Col style={{ textAlign: 'end' }}>
                {!isLastStep ? (
                  /* Always-enabled: click runs `nextStep()`, which
                     validates the step. Invalid → inline red errors
                     appear under each field. Matches Trunks / Companies. */
                  <Button
                    variant="primary"
                    onClick={nextStep}
                    style={{ padding: '0.5rem 1.75rem', fontWeight: 500 }}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    onClick={handleFormSubmit}
                    variant="success"
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                    style={{
                      padding: '0.5rem 1.75rem',
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                    }}
                    disabled={buttonDisable}
                  >
                    {buttonDisable && (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    )}
                    {isEdit
                      ? buttonDisable ? 'Updating...' : 'Update Campaign'
                      : buttonDisable ? 'Creating...' : 'Create Outbound Campaign'}
                  </Button>
                )}
              </Col>
            </Row>
          </Card.Footer>
        </Card>
      </Container>
      {/* Compact vertical rhythm inside the wizard body. Scoped to the
          campaigns-create-page container so it doesn't leak elsewhere. */}
      <style>{`
        .campaigns-create-page .card-body .form-group { margin-bottom: 1rem !important; }
        .campaigns-create-page .card-body .form-label { margin-bottom: 0.25rem !important; }
        .campaigns-create-page .card-body hr { margin: 1rem 0 !important; }
        /* Basics step top row: 3 columns for Dialer Type, Template,
           First Call Connect. Falls back to 2 cols on tablets and 1
           on phones. General fields underneath keep their normal
           full-width flow (Campaign Name reads best full-width). */
        .campaigns-basics-topgrid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0 1.25rem;
        }
        .campaigns-basics-topgrid > div { min-width: 0; }
        @media (max-width: 991px) {
          .campaigns-basics-topgrid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 575px) {
          .campaigns-basics-topgrid { grid-template-columns: 1fr; }
        }
        /* Setup step: two columns side-by-side — Settings on the left,
           Schedule on the right — so users see both blocks together
           without scrolling. Collapses to a single column on tablets. */
        .campaigns-setup-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1rem 1.5rem;
        }
        .campaigns-setup-col { min-width: 0; }
        .campaigns-setup-heading {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: hsl(var(--muted-foreground));
          margin: 0 0 0.5rem;
          padding-bottom: 0.35rem;
          border-bottom: 1px solid hsl(var(--border) / 0.6);
        }
        @media (max-width: 991px) {
          .campaigns-setup-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </FillPage>
  );
};

export default CreateCampaign;
